<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useQaPermissions } from 'dashboard/composables/useQaPermissions';
import { useQaAlertHubTabs } from 'dashboard/composables/useQaAlertHubTabs';
import { confirmQaDestructive } from 'dashboard/composables/useQaConfirm';
import QaDashboardApi from 'dashboard/api/qaDashboard';

// QA_FE_CONFIRM_V1 + QA_FE_ALERT_I18N_V1 + QA_FE_LAYOUT_CLEAN_V1
const { t } = useI18n();
const showAlert = useAlert;
const { canManageAlerts } = useQaPermissions();
const {
  activeTab,
  tabs: alertHubTabs,
  isTab,
} = useQaAlertHubTabs('connections');

const isHubLoading = ref(false);
const isHubSaving = ref(false);
const isLogsLoading = ref(false);
const isTestSending = ref(false);
const isSimulating = ref(false);
const isConnectionSaving = ref(false);

const discoveredGroups = ref([]);
const logs = ref([]);
const connections = ref([]);
// ── connection card helpers (data already in the serialized connection) ──
const connStatus = item => {
  if (item.last_error) return 'error';
  if (item.last_verified_at) return 'connected';
  return 'unverified';
};
const botOf = item => (item && item.metadata && item.metadata.bot) || {};
const botHandle = item =>
  botOf(item).username ? `@${botOf(item).username}` : '';
const botName = item => botOf(item).first_name || '';
const connInitials = item => {
  const parts = String(item.name || '?')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const two = (parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '');
  return two.toUpperCase() || '?';
};
const reachSummary = item => {
  const s = item.destination_checks_summary || {};
  if (!s.total) return null;
  return { ok: s.ok || 0, total: s.total, allOk: (s.failed || 0) === 0 };
};

// ── destination delivery health (from GET alert_hub → destination_health) ──
const destinationHealth = ref({});
const savedDestinationIds = ref([]);
const testingDestinationId = ref('');

const DESTINATION_STATE_TONE = {
  HEALTHY: 'qa-badge qa-badge--good',
  FAILING: 'qa-badge qa-badge--bad',
  DISABLED: 'qa-badge qa-badge--muted',
  NEVER: 'qa-badge qa-badge--warn',
};

const healthOf = destination => destinationHealth.value[destination.id] || {};

const destinationStateKey = destination => {
  if (!destination.enabled) return 'DISABLED';
  const { state } = healthOf(destination);
  if (state === 'healthy') return 'HEALTHY';
  if (state === 'failing') return 'FAILING';
  return 'NEVER';
};

const destinationBadgeClass = destination =>
  DESTINATION_STATE_TONE[destinationStateKey(destination)];

const destinationStateLabel = destination =>
  t(
    `QA_DASHBOARD.ALERT_HUB.DESTINATION_STATE.${destinationStateKey(destination)}`
  );

const destinationStateDetail = destination => {
  const health = healthOf(destination);
  if (health.state === 'healthy') {
    return t('QA_DASHBOARD.ALERT_HUB.LAST_DELIVERED', {
      at: new Date(health.last_delivered_at).toLocaleString(),
    });
  }
  if (health.state === 'failing') {
    return (
      health.last_error ||
      t('QA_DASHBOARD.ALERT_HUB.LAST_FAILED', {
        at: new Date(health.last_failed_at).toLocaleString(),
      })
    );
  }
  return '';
};

// A row that has not been saved yet does not exist server-side, so a test would
// come back as no_destination_match and read like a broken connection.
const isDestinationSaved = destination =>
  savedDestinationIds.value.includes(String(destination.id));

// Reloading the hub would overwrite unsaved edits elsewhere in the form, so a
// test result only refreshes its own row. The next page load brings the
// authoritative value from destination_health.
const applyTestResultToHealth = (destinationId, result) => {
  const at = new Date().toISOString();
  let entry = null;

  if (Number(result.delivered_count || 0) > 0) {
    entry = {
      state: 'healthy',
      last_delivered_at: at,
      last_failed_at: null,
      last_error: null,
    };
  } else if (Number(result.failed_count || 0) > 0) {
    entry = {
      state: 'failing',
      last_delivered_at: null,
      last_failed_at: at,
      last_error: result.last_error || null,
    };
  }
  if (!entry) return;

  destinationHealth.value = {
    ...destinationHealth.value,
    [destinationId]: entry,
  };
};

const canTestDestination = destination =>
  canManageAlerts.value &&
  destination.enabled &&
  Boolean(destination.chatId) &&
  isDestinationSaved(destination) &&
  !testingDestinationId.value;
const settingsVersion = ref(null);
const simulateResult = ref(null);

const stats = reactive({
  delivered24h: 0,
  failed24h: 0,
  skipped24h: 0,
  deadSet24h: 0,
  activeWatches: 0,
});

const routingOptions = reactive({
  inboxes: [],
  teams: [],
  agents: [],
  channelTypes: [],
});

// What the alert pipeline really emits, from Qa::Alerts::IncidentTypeCatalog. The
// older issueCatalog is a taxonomy, not an emitter list, so it offered types no
// detector sends and omitted six that it does.
const incidentTypes = ref([]);
const wildcardType = ref(null);
const severityValues = ref(['low', 'medium', 'high', 'critical']);
const routeCoverage = ref({ window_days: 7, types: {}, routes: {} });
const routePreviews = ref({});

const hubForm = reactive({
  enabled: false,
  workforceInactivitySeconds: 900,
  slowReplySeconds: 600,
  assignmentFirstReplyMinutes: 10,
  defaultConnectionId: '',
  recoveryNotifications: false,
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '08:00',
    weekdaysText: '0,1,2,3,4,5,6',
  },
  escalation: {
    enabled: false,
    afterMinutes: 15,
    destinationIdsText: '',
  },
  detectors: {
    assignment_first_reply: {
      enabled: true,
      cooldownSeconds: 300,
      maxReminders: 2,
      timeoutMinutes: 10,
    },
    slow_reply: {
      enabled: true,
      cooldownSeconds: 300,
      maxReminders: 2,
      timeoutMinutes: 10,
    },
    agent_inactivity: {
      enabled: true,
      cooldownSeconds: 300,
      maxReminders: 2,
      timeoutMinutes: 15,
    },
  },
  destinations: [],
  routes: [],
});

const connectionForm = reactive({
  name: '',
  source: 'custom',
  botToken: '',
  enabled: true,
});

const simulateForm = reactive({
  incidentType: 'safety_language',
  incidentStatus: 'pending_review',
  severity: 'medium',
  inboxId: '',
  teamId: '',
  userId: '',
  channelType: '',
});

const testForm = reactive({
  destinationId: '',
  incidentType: 'safety_language',
  incidentStatus: 'pending_review',
  severity: 'medium',
  title: 'QA Alert Hub test',
  body: 'This is a manual test alert.',
});

const logFilters = reactive({
  status: 'all',
  incidentType: '',
  destinationId: '',
  routeId: 'all',
  query: '',
  page: 1,
  perPage: 25,
});

const logMeta = reactive({
  totalCount: 0,
  totalPages: 1,
});

const destinationOptions = computed(() =>
  hubForm.destinations.map(item => ({
    id: item.id,
    label: `${item.name || item.id} (${item.chatId || '-'})`,
  }))
);

const nextId = prefix =>
  `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

const splitCsv = value =>
  String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const routeFilterOptions = computed(() =>
  hubForm.routes.map((route, index) => {
    const statuses = toArray(route.statuses);
    // Human-readable label (typeLabel + prettified statuses); the raw route id stays
    // the option VALUE so log filtering keeps working. typeLabel/prettyStatus are
    // defined below but this computed only runs when accessed, so the refs resolve.
    const parts = [
      route.incidentType ? typeLabel(route.incidentType) : `Rule ${index + 1}`,
    ];
    if (statuses.length) parts.push(statuses.map(prettyStatus).join(', '));
    return {
      id: route.id,
      label: parts.join(' · '),
    };
  })
);

const toArray = value => (Array.isArray(value) ? value : []);

// ── which bot the groups belong to ──
// Mirrors Telegram::QaAlertHubService#connection_token: the default connection
// when one is chosen and enabled, otherwise the first enabled one. Listing any
// other bot's groups is what made the old picker offer chats the sending bot was
// not even a member of.
const connectionGroups = ref({});
const groupToConnect = ref('');
const editingDestinationId = ref('');

const activeConnection = computed(() => {
  const enabled = connections.value.filter(item => item.enabled);
  const chosen = enabled.find(
    item => String(item.id) === String(hubForm.defaultConnectionId)
  );
  return chosen || enabled[0] || null;
});

const activeBotHandle = computed(() => {
  const username = activeConnection.value?.metadata?.bot?.username;
  return username ? `@${username}` : '';
});

const availableGroups = computed(() => {
  const connection = activeConnection.value;
  if (!connection) return [];

  return toArray(connectionGroups.value[String(connection.id)]).filter(
    group => group.can_post
  );
});

const connectedChatIds = computed(() =>
  hubForm.destinations.map(destination => String(destination.chatId))
);

const connectableGroups = computed(() =>
  availableGroups.value.filter(
    group => !connectedChatIds.value.includes(String(group.id))
  )
);

// The "Discovered Telegram Groups" table should surface only groups an alert bot
// can actually post to. The raw discovered_groups list also carries chats found by
// unrelated Telegram *inbox* bots (a different bot username), which are noise here.
// Keep the ids that connection_groups (bot-matched + can_post) actually offers; the
// whole section hides itself when none remain.
const usableDiscoveredIds = computed(() => {
  const ids = new Set();
  Object.values(connectionGroups.value || {}).forEach(list => {
    toArray(list).forEach(group => {
      if (group && group.can_post) ids.add(String(group.id));
    });
  });
  return ids;
});
const relevantDiscoveredGroups = computed(() =>
  toArray(discoveredGroups.value).filter(group =>
    usableDiscoveredIds.value.has(String(group.id))
  )
);

// The full walkthrough belongs to the genuinely-empty case only. With a
// destination already delivering, claiming the bot is in no group contradicts
// what the page shows one row below.
const showGroupHowto = computed(
  () =>
    Boolean(activeConnection.value) &&
    !connectableGroups.value.length &&
    !hubForm.destinations.length
);

const showNoNewGroups = computed(
  () => Boolean(activeConnection.value) && !connectableGroups.value.length
);

const connectGroup = chatId => {
  const group = availableGroups.value.find(
    item => String(item.id) === String(chatId)
  );
  if (!group) return;

  hubForm.destinations.push({
    id: nextId('dest'),
    name: group.title || group.username || String(group.id),
    chatId: String(group.id),
    messageThreadId: '',
    destinationType: group.type === 'channel' ? 'channel' : 'group',
    enabled: true,
  });
  groupToConnect.value = '';
};

const toggleDestinationEdit = destinationId => {
  editingDestinationId.value =
    editingDestinationId.value === destinationId ? '' : destinationId;
};

const groupLabel = group =>
  `${group.title || group.username || group.id} · ${group.type}`;

const parseIntegerArray = values =>
  toArray(values || [])
    .map(item => Number(item))
    .filter(item => Number.isInteger(item) && item > 0);

const parseWeekdaysCsv = value =>
  splitCsv(value)
    .map(item => Number(item))
    .filter(item => Number.isInteger(item) && item >= 0 && item <= 6);

// Quiet-hours weekday chips + escalation destination checklist. These edit the same
// CSV-backed fields the save path already reads (parseWeekdaysCsv / splitCsv), so the
// persisted payload is unchanged — only the input surface becomes friendly.
const WEEKDAY_CHIPS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];
const isQuietWeekday = day =>
  parseWeekdaysCsv(hubForm.quietHours.weekdaysText).includes(day);
const toggleQuietWeekday = day => {
  const set = new Set(parseWeekdaysCsv(hubForm.quietHours.weekdaysText));
  if (set.has(day)) set.delete(day);
  else set.add(day);
  hubForm.quietHours.weekdaysText = [...set].sort((a, b) => a - b).join(', ');
};
const isEscalationDest = id =>
  splitCsv(hubForm.escalation.destinationIdsText).includes(String(id));
const toggleEscalationDest = id => {
  const set = new Set(
    splitCsv(hubForm.escalation.destinationIdsText).map(String)
  );
  const key = String(id);
  if (set.has(key)) set.delete(key);
  else set.add(key);
  hubForm.escalation.destinationIdsText = [...set].join(', ');
};
const quietHoursSummary = computed(() => {
  if (!hubForm.quietHours.enabled) return 'Off';
  return `${hubForm.quietHours.start || '22:00'}–${hubForm.quietHours.end || '08:00'}`;
});
const escalationSummary = computed(() => {
  if (!hubForm.escalation.enabled) return 'Off';
  return `after ${Number(hubForm.escalation.afterMinutes) || 15}m`;
});

// These two are stored in seconds and are now edited in seconds. They used to be
// shown in minutes, converted on the way in and out, purely to match the
// assignment-first-reply field beside them — but reply targets here are written
// in seconds, and dividing by 60 to display made a 45-second target read as
// 0.75. Assignment first reply is genuinely stored in minutes and is left alone.

const parseTimeWindowsCsv = value =>
  splitCsv(value)
    .map(item => {
      const [start, end] = item.split('-').map(part => part.trim());
      if (!start || !end) return null;
      if (!start.match(/^([01]\d|2[0-3]):[0-5]\d$/)) return null;
      if (!end.match(/^([01]\d|2[0-3]):[0-5]\d$/)) return null;
      return { start, end };
    })
    .filter(Boolean);

const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

const applyRoutePreset = (route, preset) => {
  if (!route) return;
  if (preset === 'weekdays_office') {
    route.weekdays = [1, 2, 3, 4, 5];
    route.timeWindowsText = '09:00-18:00';
    return;
  }

  if (preset === 'all_day') {
    route.weekdays = [...ALL_WEEKDAYS];
    route.timeWindowsText = '00:00-23:59';
    return;
  }

  if (preset === 'night_shift') {
    route.weekdays = [...ALL_WEEKDAYS];
    route.timeWindowsText = '18:00-09:00';
    return;
  }

  route.weekdays = [];
  route.timeWindowsText = '';
};

const routingSummary = log => {
  const routing = log?.payload?.routing || log?.metadata?.routing || {};
  const strategy = String(routing.strategy || '').trim();
  const destinationRouteIds = toArray(
    routing.destination_route_ids || routing.destinationRouteIds || []
  ).filter(Boolean);
  const matchedRouteIds = toArray(
    routing.matched_route_ids || routing.matchedRouteIds || []
  ).filter(Boolean);
  const activeRouteIds = destinationRouteIds.length
    ? destinationRouteIds
    : matchedRouteIds;

  if (strategy === 'direct_destination')
    return t('QA_DASHBOARD.ALERT_HUB.DIRECT_DESTINATION');
  if (!activeRouteIds.length) return t('QA_DASHBOARD.ALERT_HUB.ROUTE_MATCHING');
  return `Route IDs: ${activeRouteIds.join(', ')}`;
};

const routeIdsForLog = log => {
  const routing = log?.payload?.routing || log?.metadata?.routing || {};
  const destinationRouteIds = toArray(
    routing.destination_route_ids || routing.destinationRouteIds || []
  ).filter(Boolean);
  const matchedRouteIds = toArray(
    routing.matched_route_ids || routing.matchedRouteIds || []
  ).filter(Boolean);
  return (
    destinationRouteIds.length ? destinationRouteIds : matchedRouteIds
  ).map(item => String(item));
};

const filterByRoute = async routeId => {
  if (!routeId) return;
  logFilters.routeId = String(routeId);
  logFilters.page = 1;
  await loadLogs();
};

const routingContextSummary = log => {
  const routing = log?.payload?.routing || log?.metadata?.routing || {};
  const context = routing.context || {};
  const parts = [];
  if (context.inbox_id) parts.push(`inbox:${context.inbox_id}`);
  if (context.team_id) parts.push(`team:${context.team_id}`);
  if (context.user_id) parts.push(`user:${context.user_id}`);
  if (context.channel_type) parts.push(context.channel_type);
  if (context.occurred_at)
    parts.push(new Date(context.occurred_at).toLocaleString());
  return parts.join(' | ') || '-';
};

const logStatusBadgeClass = status => {
  const value = String(status || '').toLowerCase();
  if (value === 'delivered') return 'qa-badge qa-badge--good';
  if (value === 'failed') return 'qa-badge qa-badge--bad';
  if (value === 'skipped') return 'qa-badge qa-badge--warn';
  return 'qa-badge qa-badge--muted';
};

const fillHubForm = item => {
  const data = item || {};
  hubForm.enabled = Boolean(data.enabled);
  hubForm.workforceInactivitySeconds = Number(
    data.workforce_inactivity_seconds || 900
  );
  hubForm.slowReplySeconds = Number(data.slow_reply_seconds || 600);
  hubForm.assignmentFirstReplyMinutes = Number(
    data.assignment_first_reply_minutes || 10
  );
  hubForm.defaultConnectionId = data.default_connection_id
    ? String(data.default_connection_id)
    : '';
  hubForm.recoveryNotifications = Boolean(data.recovery_notifications);

  const quiet = data.quiet_hours || {};
  hubForm.quietHours.enabled = Boolean(quiet.enabled);
  hubForm.quietHours.start = quiet.start || '22:00';
  hubForm.quietHours.end = quiet.end || '08:00';
  hubForm.quietHours.weekdaysText = toArray(
    quiet.weekdays || [0, 1, 2, 3, 4, 5, 6]
  ).join(', ');

  const escalation = data.escalation || {};
  hubForm.escalation.enabled = Boolean(escalation.enabled);
  hubForm.escalation.afterMinutes = Number(escalation.after_minutes || 15);
  hubForm.escalation.destinationIdsText = toArray(
    escalation.destination_ids || []
  ).join(', ');

  const detectors = data.detectors || {};
  ['assignment_first_reply', 'slow_reply', 'agent_inactivity'].forEach(key => {
    const row = detectors[key] || {};
    hubForm.detectors[key] = {
      enabled: row.enabled !== false,
      cooldownSeconds: Number(row.cooldown_seconds || 300),
      maxReminders: Number(row.max_reminders || 2),
      timeoutMinutes: Number(row.timeout_minutes || 10),
    };
  });

  hubForm.destinations = toArray(data.destinations || []).map(row => ({
    id: row.id || nextId('dest'),
    name: row.name || '',
    chatId: row.chat_id || '',
    messageThreadId: row.message_thread_id || '',
    destinationType:
      row.destination_type || (row.message_thread_id ? 'topic' : 'group'),
    enabled: row.enabled !== false,
  }));

  hubForm.routes = toArray(data.routes || []).map(row => ({
    id: row.id || nextId('route'),
    incidentType: row.incident_type || '',
    statuses: toArray(row.statuses || []).map(item => String(item)),
    severities: toArray(row.severities || []).map(item => String(item)),
    destinationIds: toArray(row.destination_ids || []).map(item =>
      String(item)
    ),
    priority: Number(row.priority || 100),
    stopProcessing: Boolean(row.stop_processing),
    cooldownSeconds: Number(row.cooldown_seconds || 0),
    digestWindowSeconds: Number(row.digest_window_seconds || 0),
    inboxIds: toArray(row.conditions?.inbox_ids || []).map(item =>
      String(item)
    ),
    teamIds: toArray(row.conditions?.team_ids || []).map(item => String(item)),
    userIds: toArray(row.conditions?.user_ids || []).map(item => String(item)),
    channelTypes: toArray(row.conditions?.channel_types || []).map(item =>
      String(item)
    ),
    weekdays: toArray(row.conditions?.weekdays || [])
      .map(item => Number(item))
      .filter(item => ALL_WEEKDAYS.includes(item)),
    timeWindowsText: toArray(row.conditions?.time_windows || [])
      .map(window => `${window.start}-${window.end}`)
      .join(', '),
    enabled: row.enabled !== false,
  }));
};

const hubPayload = () => ({
  settings_version: settingsVersion.value,
  enabled: hubForm.enabled,
  workforce_inactivity_seconds:
    Number(hubForm.workforceInactivitySeconds) || 900,
  slow_reply_seconds: Number(hubForm.slowReplySeconds) || 600,
  assignment_first_reply_minutes:
    Number(hubForm.assignmentFirstReplyMinutes) || 10,
  default_connection_id: hubForm.defaultConnectionId || null,
  recovery_notifications: hubForm.recoveryNotifications,
  quiet_hours: {
    enabled: hubForm.quietHours.enabled,
    start: hubForm.quietHours.start,
    end: hubForm.quietHours.end,
    weekdays: parseWeekdaysCsv(hubForm.quietHours.weekdaysText),
  },
  escalation: {
    enabled: hubForm.escalation.enabled,
    after_minutes: Number(hubForm.escalation.afterMinutes) || 15,
    destination_ids: splitCsv(hubForm.escalation.destinationIdsText),
  },
  detectors: {
    assignment_first_reply: {
      enabled: hubForm.detectors.assignment_first_reply.enabled,
      cooldown_seconds:
        Number(hubForm.detectors.assignment_first_reply.cooldownSeconds) || 300,
      max_reminders:
        Number(hubForm.detectors.assignment_first_reply.maxReminders) || 2,
      timeout_minutes:
        Number(hubForm.detectors.assignment_first_reply.timeoutMinutes) || 10,
    },
    slow_reply: {
      enabled: hubForm.detectors.slow_reply.enabled,
      cooldown_seconds:
        Number(hubForm.detectors.slow_reply.cooldownSeconds) || 300,
      max_reminders: Number(hubForm.detectors.slow_reply.maxReminders) || 2,
      timeout_minutes:
        Number(hubForm.detectors.slow_reply.timeoutMinutes) || 10,
    },
    agent_inactivity: {
      enabled: hubForm.detectors.agent_inactivity.enabled,
      cooldown_seconds:
        Number(hubForm.detectors.agent_inactivity.cooldownSeconds) || 300,
      max_reminders:
        Number(hubForm.detectors.agent_inactivity.maxReminders) || 2,
      timeout_minutes:
        Number(hubForm.detectors.agent_inactivity.timeoutMinutes) || 15,
    },
  },
  destinations: hubForm.destinations.map(row => ({
    id: row.id,
    name: row.name,
    chat_id: row.chatId,
    message_thread_id: row.messageThreadId || null,
    destination_type: row.destinationType || 'group',
    enabled: row.enabled,
  })),
  routes: hubForm.routes.map(row => ({
    id: row.id,
    incident_type: row.incidentType,
    statuses: toArray(row.statuses),
    severities: toArray(row.severities),
    destination_ids: toArray(row.destinationIds),
    priority: Number(row.priority) || 100,
    stop_processing: Boolean(row.stopProcessing),
    cooldown_seconds: Number(row.cooldownSeconds) || 0,
    digest_window_seconds: Number(row.digestWindowSeconds) || 0,
    conditions: {
      inbox_ids: parseIntegerArray(row.inboxIds),
      team_ids: parseIntegerArray(row.teamIds),
      user_ids: parseIntegerArray(row.userIds),
      channel_types: toArray(row.channelTypes || [])
        .map(item => String(item).trim())
        .filter(Boolean),
      weekdays: toArray(row.weekdays).map(item => Number(item)),
      time_windows: parseTimeWindowsCsv(row.timeWindowsText),
    },
    enabled: row.enabled,
  })),
});

const loadHub = async () => {
  isHubLoading.value = true;
  try {
    const response = await QaDashboardApi.getAlertHub();
    fillHubForm(response?.data?.item || {});
    settingsVersion.value = response?.data?.settings_version ?? null;
    discoveredGroups.value = response?.data?.discovered_groups || [];
    connections.value = response?.data?.connections || [];
    const catalog = response?.data?.incident_types || {};
    incidentTypes.value = catalog.types || [];
    wildcardType.value = catalog.wildcard || null;
    if (catalog.severities?.length) severityValues.value = catalog.severities;
    routeCoverage.value = response?.data?.route_coverage || {
      window_days: 7,
      types: {},
      routes: {},
    };
    routePreviews.value = {};
    destinationHealth.value = response?.data?.destination_health || {};
    connectionGroups.value = response?.data?.connection_groups || {};
    savedDestinationIds.value = (response?.data?.item?.destinations || []).map(
      row => String(row.id)
    );
    const options = response?.data?.routing_options || {};
    routingOptions.inboxes = options.inboxes || [];
    routingOptions.teams = options.teams || [];
    routingOptions.agents = options.agents || [];
    routingOptions.channelTypes = options.channel_types || [];

    if (
      logFilters.routeId !== 'all' &&
      !routeFilterOptions.value.some(item => item.id === logFilters.routeId)
    ) {
      logFilters.routeId = 'all';
    }

    const newStats = response?.data?.stats || {};
    stats.delivered24h = Number(newStats.delivered_24h || 0);
    stats.failed24h = Number(newStats.failed_24h || 0);
    stats.skipped24h = Number(newStats.skipped_24h || 0);
    stats.deadSet24h = Number(newStats.dead_set_24h || 0);
    stats.activeWatches = Number(newStats.active_watches || 0);
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.ALERT_HUB.ERROR_LOAD_SETTINGS')
    );
  } finally {
    isHubLoading.value = false;
  }
};

const saveHub = async () => {
  isHubSaving.value = true;
  try {
    const response = await QaDashboardApi.updateAlertHub(hubPayload());
    settingsVersion.value =
      response?.data?.settings_version ?? settingsVersion.value;
    await loadHub();
    showAlert(t('QA_DASHBOARD.ALERT_HUB.SUCCESS_SETTINGS_SAVED'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.ALERT_HUB.ERROR_SAVE_SETTINGS')
    );
  } finally {
    isHubSaving.value = false;
  }
};

const sendTest = async () => {
  isTestSending.value = true;
  try {
    const payload = {
      destination_id: testForm.destinationId || undefined,
      incident_type: testForm.incidentType,
      incident_status: testForm.incidentStatus,
      severity: testForm.severity,
      title: testForm.title,
      body: testForm.body,
    };
    const response = await QaDashboardApi.testAlertHubDelivery(payload);
    const item = response?.data?.item || {};
    if (item.skipped) {
      showAlert(
        t('QA_DASHBOARD.ALERT_HUB.TEST_SKIPPED', {
          reason: item.reason || t('QA_DASHBOARD.ALERT_HUB.UNKNOWN_REASON'),
        })
      );
    } else {
      const matchedRouteIds = toArray(item.matched_route_ids || []);
      const routeInfo = matchedRouteIds.length
        ? `, routes=${matchedRouteIds.join('/')}`
        : '';
      showAlert(
        t('QA_DASHBOARD.ALERT_HUB.TEST_SENT', {
          delivered: item.delivered_count || 0,
          failed: item.failed_count || 0,
          routes: routeInfo,
        })
      );
    }
    await loadLogs();
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.ALERT_HUB.ERROR_SEND_TEST')
    );
  } finally {
    isTestSending.value = false;
  }
};

const logParams = () => {
  const params = {
    page: logFilters.page,
    per_page: logFilters.perPage,
  };
  if (logFilters.status !== 'all') params.status = logFilters.status;
  if (logFilters.incidentType.trim())
    params.incident_type = logFilters.incidentType.trim();
  if (logFilters.destinationId.trim())
    params.destination_id = logFilters.destinationId.trim();
  if (logFilters.routeId !== 'all') params.route_id = logFilters.routeId;
  if (logFilters.query.trim()) params.q = logFilters.query.trim();
  return params;
};

const loadLogs = async () => {
  isLogsLoading.value = true;
  try {
    const response = await QaDashboardApi.getAlertLogs(logParams());
    logs.value = response?.data?.items || [];
    const meta = response?.data?.meta || {};
    logMeta.totalCount = Number(meta.total_count || 0);
    logMeta.totalPages = Number(meta.total_pages || 1);
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.ALERT_HUB.ERROR_LOAD_LOGS')
    );
  } finally {
    isLogsLoading.value = false;
  }
};

const onLogFilterChange = async () => {
  logFilters.page = 1;
  await loadLogs();
};

const goToLogPage = async page => {
  if (page < 1 || page > logMeta.totalPages) return;
  logFilters.page = page;
  await loadLogs();
};

const addDestination = () => {
  hubForm.destinations.push({
    id: nextId('dest'),
    name: '',
    chatId: '',
    messageThreadId: '',
    destinationType: 'group',
    enabled: true,
  });
};

const removeDestination = destinationId => {
  hubForm.destinations = hubForm.destinations.filter(
    item => item.id !== destinationId
  );
};

// Posts a real message to the group, so it is gated behind a confirm.
const sendDestinationTest = async destination => {
  if (
    !confirmQaDestructive({
      action: t('QA_DASHBOARD.ALERT_HUB.CONFIRM_TEST_ACTION'),
      subject: destination.name || destination.chatId || destination.id,
      detail: t('QA_DASHBOARD.ALERT_HUB.CONFIRM_TEST_DETAIL'),
    })
  ) {
    return;
  }

  testingDestinationId.value = destination.id;
  try {
    const response = await QaDashboardApi.testAlertHubDelivery({
      destination_id: destination.id,
      incident_type: testForm.incidentType,
      incident_status: testForm.incidentStatus,
      severity: testForm.severity,
      title: testForm.title,
      body: testForm.body,
    });
    const item = response?.data?.item || {};
    if (item.skipped) {
      showAlert(
        t('QA_DASHBOARD.ALERT_HUB.TEST_SKIPPED', {
          reason: item.reason || t('QA_DASHBOARD.ALERT_HUB.UNKNOWN_REASON'),
        })
      );
    } else {
      showAlert(
        t('QA_DASHBOARD.ALERT_HUB.TEST_SENT', {
          delivered: item.delivered_count || 0,
          failed: item.failed_count || 0,
          routes: '',
        })
      );
    }
    applyTestResultToHealth(destination.id, item);
    await loadLogs();
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.ALERT_HUB.ERROR_SEND_TEST')
    );
  } finally {
    testingDestinationId.value = '';
  }
};

// Defaults deliberately leave every filter empty: an empty filter accepts
// everything, so a fresh rule delivers instead of silently matching nothing.
// The old default pre-filled safety statuses, which broke as soon as the type
// was changed to one that reports different statuses.
const addRoute = (incidentType = 'safety_language') => {
  const onlyDestination =
    hubForm.destinations.length === 1
      ? [String(hubForm.destinations[0].id)]
      : [];

  hubForm.routes.push({
    id: nextId('route'),
    incidentType,
    statuses: [],
    severities: [],
    destinationIds: onlyDestination,
    priority: 100,
    stopProcessing: false,
    cooldownSeconds: 1800,
    digestWindowSeconds: 0,
    inboxIds: [],
    teamIds: [],
    userIds: [],
    channelTypes: [],
    weekdays: [],
    timeWindowsText: '',
    enabled: true,
  });
};

const removeRoute = routeId => {
  hubForm.routes = hubForm.routes.filter(item => item.id !== routeId);
};

const ROUTING_I18N = 'QA_DASHBOARD.ALERT_HUB.ROUTING';

const typeMeta = key => {
  const value = String(key || '');
  if (value === '*') return wildcardType.value;
  return incidentTypes.value.find(row => row.key === value) || null;
};

const typeLabel = key => {
  const value = String(key || '');
  if (value === '*') return t(`${ROUTING_I18N}.ANY_TYPE`);
  if (!typeMeta(value)) return t(`${ROUTING_I18N}.CUSTOM_TYPE`, { key: value });
  return t(`${ROUTING_I18N}.TYPE_${value.toUpperCase()}`);
};

const statusLabel = value =>
  t(`${ROUTING_I18N}.STATUS_${String(value).toUpperCase()}`);
const severityLabel = value =>
  t(`${ROUTING_I18N}.SEVERITY_${String(value).toUpperCase()}`);
const KNOWN_SKIP_REASONS = [
  'no_route_configured',
  'route_filtered',
  'quiet_hours_blocked',
  'cooldown_active',
  'no_destination_match',
  'no_route_match',
  'alert_hub_disabled',
  'missing_bot_token',
  'shadow_mode',
  'rollout_blocked',
];

const skipReasonLabel = value => {
  const reason = String(value || '');
  if (!KNOWN_SKIP_REASONS.includes(reason))
    return t(`${ROUTING_I18N}.REASON_UNKNOWN`);
  return t(`${ROUTING_I18N}.REASON_${reason.toUpperCase()}`);
};

const typeGroups = computed(() => {
  const order = ['safety', 'template', 'sla', 'workforce', 'report'];
  return order
    .map(group => ({
      group,
      label: t(`${ROUTING_I18N}.GROUP_${group.toUpperCase()}`),
      items: incidentTypes.value.filter(row => row.group === group),
    }))
    .filter(entry => entry.items.length);
});

// Values already saved stay selectable even when the catalog does not know them,
// so opening the page can never quietly drop part of an existing rule.
const statusOptionsFor = route => {
  const meta = typeMeta(route.incidentType);
  return Array.from(
    new Set([...(meta?.statuses || []), ...toArray(route.statuses)])
  );
};

const severityOptionsFor = route => {
  const meta = typeMeta(route.incidentType);
  const known = meta?.severities?.length
    ? meta.severities
    : severityValues.value;
  return Array.from(new Set([...known, ...toArray(route.severities)]));
};

// Delivery tab (Route simulator / Test delivery): status options follow the picked
// incident type (like statusOptionsFor above, but for a plain type key rather than a
// route). resetStatusFor keeps the status valid when the type changes.
const allDeliveryStatuses = computed(() => {
  const set = new Set();
  incidentTypes.value.forEach(row =>
    (row.statuses || []).forEach(st => set.add(st))
  );
  return [...set];
});
const statusesForType = key => {
  const meta = typeMeta(key);
  return meta && (meta.statuses || []).length
    ? meta.statuses
    : allDeliveryStatuses.value;
};
const resetStatusFor = form => {
  const opts = statusesForType(form.incidentType);
  if (opts.length && !opts.includes(form.incidentStatus))
    form.incidentStatus = opts[0];
};
const prettyStatus = st => String(st || '').replace(/_/g, ' ');
const routeLabelById = id => {
  const found = routeFilterOptions.value.find(
    opt => String(opt.id) === String(id)
  );
  return found ? found.label : `#${id}`;
};

const filterAllowed = (route, filter) => {
  const meta = typeMeta(route.incidentType);
  if (!meta) return true;
  return meta.filters.includes(filter);
};

const toggleInArray = (list, value) => {
  const index = list.indexOf(value);
  if (index === -1) list.push(value);
  else list.splice(index, 1);
};

const timeWindowsValid = route => {
  const raw = String(route.timeWindowsText || '').trim();
  if (!raw) return true;
  return raw
    .split(',')
    .every(part =>
      /^\s*([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d\s*$/.test(part)
    );
};

const mergeSkipReasons = (into, from) => {
  Object.entries(from || {}).forEach(([reason, count]) => {
    into[reason] = (into[reason] || 0) + Number(count || 0);
  });
  return into;
};

const coverageFor = route => {
  const types = routeCoverage.value.types || {};
  if (String(route.incidentType) === '*') {
    return Object.values(types).reduce(
      (sum, row) => ({
        total: sum.total + row.total,
        delivered: sum.delivered + row.delivered,
        skipped: sum.skipped + row.skipped,
        skip_reasons: mergeSkipReasons(sum.skip_reasons, row.skip_reasons),
      }),
      { total: 0, delivered: 0, skipped: 0, skip_reasons: {} }
    );
  }
  return types[route.incidentType] || null;
};

// The reason breakdown is what turns "642 dropped" into something actionable;
// without it the panel repeats the number the KPI card already showed.
const skipReasonsFor = route => {
  const coverage = coverageFor(route);
  return Object.entries(coverage?.skip_reasons || {})
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([reason, count]) => ({
      reason,
      count,
      label: skipReasonLabel(reason),
    }));
};

const deliveriesFor = route =>
  Number(routeCoverage.value.routes?.[route.id] || 0);

const unsupportedFilterWarnings = (route, meta) =>
  [
    ['inbox', route.inboxIds],
    ['team', route.teamIds],
    ['channel', route.channelTypes],
    ['agent', route.userIds],
  ]
    .filter(
      ([name, values]) => toArray(values).length && !meta.filters.includes(name)
    )
    .map(([name]) => ({
      key: 'WARN_FILTER_UNSUPPORTED',
      params: { filter: t(`${ROUTING_I18N}.FILTER_${name.toUpperCase()}`) },
    }));

const routeWarnings = route => {
  const key = String(route.incidentType || '').trim();
  const meta = typeMeta(key);
  const warnings = [];

  if (!meta)
    warnings.push({ key: 'WARN_UNKNOWN_TYPE', params: { key: key || '—' } });
  if (meta?.delivery === 'escalation_settings')
    warnings.push({ key: 'WARN_BYPASS_ESCALATION' });
  if (meta?.delivery === 'daily_report_settings')
    warnings.push({ key: 'WARN_BYPASS_REPORT' });
  if (!toArray(route.destinationIds).length)
    warnings.push({ key: 'WARN_NO_DESTINATION' });
  if (!route.enabled) warnings.push({ key: 'WARN_DISABLED' });
  if (meta && !meta.statuses.length && toArray(route.statuses).length) {
    warnings.push({ key: 'WARN_NO_STATUS' });
  }
  if (meta?.statuses.length) {
    toArray(route.statuses)
      .filter(value => !meta.statuses.includes(value))
      .forEach(value =>
        warnings.push({ key: 'WARN_STATUS_UNKNOWN', params: { value } })
      );
  }
  if (
    meta?.requires_setting === 'recovery_notifications' &&
    !hubForm.recoveryNotifications
  ) {
    warnings.push({ key: 'WARN_RECOVERY_OFF' });
  }
  if (meta) warnings.push(...unsupportedFilterWarnings(route, meta));

  return warnings;
};

// Alert types that arrived, were dropped, and have no rule pointing at them. This
// is the gap that left 708 slow_reply and assignment_first_reply alerts unsent
// with nothing on screen to explain it.
const routingGaps = computed(() => {
  const covered = new Set(
    hubForm.routes
      .filter(route => route.enabled)
      .map(route => String(route.incidentType))
  );
  if (covered.has('*')) return [];

  return Object.entries(routeCoverage.value.types || {})
    .map(([key, row]) => ({ key, count: row.skipped }))
    .filter(row => row.count > 0 && !covered.has(row.key))
    .filter(row => {
      const meta = typeMeta(row.key);
      return !meta || meta.delivery === 'routed';
    })
    .sort((a, b) => b.count - a.count);
});

const routingGapTotal = computed(() =>
  routingGaps.value.reduce((sum, row) => sum + row.count, 0)
);

const previewOf = route => routePreviews.value[route.id] || null;

const setPreview = (routeId, value) => {
  routePreviews.value = { ...routePreviews.value, [routeId]: value };
};

const previewRoute = async route => {
  setPreview(route.id, { loading: true });
  try {
    const response = await QaDashboardApi.previewAlertHubRoute({
      days: routeCoverage.value.window_days || 7,
      route: {
        incident_type: route.incidentType,
        statuses: toArray(route.statuses),
        severities: toArray(route.severities),
        conditions: {
          inbox_ids: parseIntegerArray(route.inboxIds),
          team_ids: parseIntegerArray(route.teamIds),
          user_ids: parseIntegerArray(route.userIds),
          channel_types: toArray(route.channelTypes),
          weekdays: toArray(route.weekdays),
          time_windows: parseTimeWindowsCsv(route.timeWindowsText),
        },
      },
    });
    setPreview(route.id, {
      loading: false,
      result: response?.data?.item || null,
    });
  } catch (error) {
    setPreview(route.id, { loading: false });
    showAlert(
      error.response?.data?.error || t(`${ROUTING_I18N}.PREVIEW_ERROR`)
    );
  }
};

const createConnection = async () => {
  if (!connectionForm.name.trim() || !connectionForm.botToken.trim()) {
    showAlert(t('QA_DASHBOARD.ALERT_HUB.ERROR_NAME_TOKEN_REQUIRED'));
    return;
  }
  isConnectionSaving.value = true;
  try {
    await QaDashboardApi.createAlertConnection({
      name: connectionForm.name.trim(),
      source: connectionForm.source,
      bot_token: connectionForm.botToken.trim(),
      enabled: connectionForm.enabled,
    });
    connectionForm.name = '';
    connectionForm.botToken = '';
    await loadHub();
    showAlert(t('QA_DASHBOARD.ALERT_HUB.SUCCESS_CONNECTION_CREATED'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.ALERT_HUB.ERROR_CREATE_CONNECTION')
    );
  } finally {
    isConnectionSaving.value = false;
  }
};

const verifyConnection = async connectionId => {
  try {
    const response = await QaDashboardApi.verifyAlertConnection(connectionId);
    const verified = response?.data?.verified;
    showAlert(
      verified
        ? t('QA_DASHBOARD.ALERT_HUB.SUCCESS_VERIFIED')
        : t('QA_DASHBOARD.ALERT_HUB.ERROR_VERIFY_FAILED')
    );
    await loadHub();
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.ALERT_HUB.ERROR_VERIFY')
    );
  }
};

const deleteConnection = async connectionId => {
  if (
    !confirmQaDestructive({
      action: 'delete alert connection',
      subject: `connection #${connectionId}`,
    })
  ) {
    return;
  }
  try {
    await QaDashboardApi.deleteAlertConnection(connectionId);
    await loadHub();
    showAlert(t('QA_DASHBOARD.ALERT_HUB.SUCCESS_CONNECTION_DELETED'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.ALERT_HUB.ERROR_DELETE_CONNECTION')
    );
  }
};

const runSimulate = async () => {
  isSimulating.value = true;
  try {
    const response = await QaDashboardApi.simulateAlertHubRoute({
      incident_type: simulateForm.incidentType,
      incident_status: simulateForm.incidentStatus,
      severity: simulateForm.severity,
      inbox_id: simulateForm.inboxId || undefined,
      team_id: simulateForm.teamId || undefined,
      user_id: simulateForm.userId || undefined,
      channel_type: simulateForm.channelType || undefined,
    });
    simulateResult.value = response?.data?.item || null;
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.ALERT_HUB.ERROR_SIMULATE')
    );
  } finally {
    isSimulating.value = false;
  }
};

onMounted(async () => {
  await loadHub();
  await loadLogs();
});
</script>

<template>
  <div class="qa-page">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.ALERT_HUB.TITLE') }}</h1>
        </div>
        <div class="qa-header-actions">
          <button
            v-if="canManageAlerts"
            type="button"
            class="qa-btn qa-btn--primary"
            data-qa-marker="save-hub"
            :disabled="isHubLoading || isHubSaving"
            @click="saveHub"
          >
            {{
              isHubSaving
                ? t('QA_DASHBOARD.ALERT_HUB.BTN_SAVING')
                : t('QA_DASHBOARD.ALERT_HUB.BTN_SAVE_HUB')
            }}
          </button>
        </div>
      </div>

      <div class="qa-subheader qa-subheader--tight">
        <p class="qa-desc">
          {{ t('QA_DASHBOARD.ALERT_HUB.DESCRIPTION') }}
        </p>
      </div>

      <div class="qa-content qa-content--tight qa-alert-hub-layout-fix">
        <div class="qa-kpis qa-kpis--compact">
          <div class="qa-kpi qa-kpi--total">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.ALERT_HUB.KPI.DELIVERED') }}
            </div>
            <div class="qa-kpi-value">{{ stats.delivered24h }}</div>
          </div>
          <div class="qa-kpi qa-kpi--tone-bad">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.ALERT_HUB.KPI.FAILED') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--bad">
              {{ stats.failed24h }}
            </div>
          </div>
          <div class="qa-kpi qa-kpi--tone-bad">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.ALERT_HUB.KPI.DEAD_SET') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--bad">
              {{ stats.deadSet24h }}
            </div>
            <div class="qa-hint">
              {{ t('QA_DASHBOARD.ALERT_HUB.KPI.DEAD_SET_HINT') }}
            </div>
          </div>
          <div class="qa-kpi qa-kpi--tone-warn">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.ALERT_HUB.KPI.SKIPPED') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--warn">
              {{ stats.skipped24h }}
            </div>
            <div class="qa-hint">
              {{ t('QA_DASHBOARD.ALERT_HUB.KPI.SKIPPED_HINT') }}
            </div>
          </div>
          <div class="qa-kpi qa-kpi--tone-neutral">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.ALERT_HUB.KPI.ACTIVE_WATCHES') }}
            </div>
            <div class="qa-kpi-value">{{ stats.activeWatches }}</div>
            <div class="qa-hint">
              {{ t('QA_DASHBOARD.ALERT_HUB.KPI.ACTIVE_WATCHES_HINT') }}
            </div>
          </div>
        </div>

        <div class="qa-section-nav" role="tablist" aria-label="Alert Hub tabs">
          <button
            v-for="tab in alertHubTabs"
            :key="tab.id"
            type="button"
            class="qa-tab"
            :class="{ 'qa-tab--active': isTab(tab.id) }"
            role="tab"
            :aria-selected="isTab(tab.id)"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <div v-show="isTab('escalations')" class="qa-tab-panel">
          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">Hub settings</h2>
              <p class="qa-hint" style="margin: 4px 0 0">
                Thresholds decide when workforce and slow-reply alerts fire.
                Each field says its own unit. The deadline is kept to the
                second, but the sweeps that send the alerts run once a minute,
                so a threshold under a minute brings the deadline forward rather
                than the message.
              </p>
            </div>
            <div class="qa-card-body">
              <div class="qa-form-grid qa-form-grid--3">
                <label class="qa-field">
                  <span class="qa-label">Alert Hub enabled</span>
                  <select v-model="hubForm.enabled" class="qa-select">
                    <option :value="true">Enabled</option>
                    <option :value="false">Disabled</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Inactivity threshold (sec)</span>
                  <input
                    v-model.number="hubForm.workforceInactivitySeconds"
                    class="qa-input"
                    type="number"
                    min="10"
                    step="10"
                  />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Slow reply threshold (sec)</span>
                  <input
                    v-model.number="hubForm.slowReplySeconds"
                    class="qa-input"
                    type="number"
                    min="10"
                    step="10"
                  />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Assignment first reply (min)</span>
                  <input
                    v-model.number="hubForm.assignmentFirstReplyMinutes"
                    class="qa-input"
                    type="number"
                    min="1"
                  />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Default bot connection</span>
                  <select
                    v-model="hubForm.defaultConnectionId"
                    class="qa-select"
                  >
                    <option value="">Auto (first enabled)</option>
                    <option
                      v-for="item in connections"
                      :key="`conn-opt-${item.id}`"
                      :value="String(item.id)"
                    >
                      {{ item.name }} ({{ item.masked_token || 'no token' }})
                    </option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Recovery notifications</span>
                  <select
                    v-model="hubForm.recoveryNotifications"
                    class="qa-select"
                  >
                    <option :value="true">Enabled</option>
                    <option :value="false">Disabled</option>
                  </select>
                </label>
              </div>

              <details class="qa-disclosure" style="margin-top: 16px">
                <summary>
                  Quiet hours
                  <span class="ah-disc-state">— {{ quietHoursSummary }}</span>
                </summary>
                <div
                  class="qa-form-grid qa-form-grid--3"
                  style="margin-top: 8px"
                >
                  <label class="qa-field">
                    <span class="qa-label">Enabled</span>
                    <select
                      v-model="hubForm.quietHours.enabled"
                      class="qa-select"
                    >
                      <option :value="true">Enabled</option>
                      <option :value="false">Disabled</option>
                    </select>
                  </label>
                  <label class="qa-field">
                    <span class="qa-label">Quiet start</span>
                    <input
                      v-model="hubForm.quietHours.start"
                      class="qa-input"
                      placeholder="22:00"
                    />
                  </label>
                  <label class="qa-field">
                    <span class="qa-label">Quiet end</span>
                    <input
                      v-model="hubForm.quietHours.end"
                      class="qa-input"
                      placeholder="08:00"
                    />
                  </label>
                  <label class="qa-field ah-stack-field">
                    <span class="qa-label">Active on days</span>
                    <div class="ah-daychips">
                      <button
                        v-for="d in WEEKDAY_CHIPS"
                        :key="`quiet-day-${d.value}`"
                        type="button"
                        class="ah-daychip"
                        :class="{ 'ah-daychip--on': isQuietWeekday(d.value) }"
                        @click="toggleQuietWeekday(d.value)"
                      >
                        {{ d.label }}
                      </button>
                    </div>
                  </label>
                </div>
              </details>

              <details class="qa-disclosure" style="margin-top: 12px">
                <summary>
                  Escalation
                  <span class="ah-disc-state">— {{ escalationSummary }}</span>
                </summary>
                <div
                  class="qa-form-grid qa-form-grid--3"
                  style="margin-top: 8px"
                >
                  <label class="qa-field">
                    <span class="qa-label">Enabled</span>
                    <select
                      v-model="hubForm.escalation.enabled"
                      class="qa-select"
                    >
                      <option :value="true">Enabled</option>
                      <option :value="false">Disabled</option>
                    </select>
                  </label>
                  <label class="qa-field">
                    <span class="qa-label">Escalate after (min)</span>
                    <input
                      v-model.number="hubForm.escalation.afterMinutes"
                      class="qa-input"
                      type="number"
                      min="1"
                    />
                  </label>
                  <label class="qa-field ah-stack-field">
                    <span class="qa-label">Escalate to</span>
                    <div v-if="hubForm.destinations.length" class="ah-destpick">
                      <label
                        v-for="dest in hubForm.destinations"
                        :key="`esc-dest-${dest.id}`"
                        class="ah-destpick-item"
                      >
                        <input
                          type="checkbox"
                          :checked="isEscalationDest(dest.id)"
                          @change="toggleEscalationDest(dest.id)"
                        />
                        <span>{{
                          dest.name || `#${dest.chatId || dest.id}`
                        }}</span>
                      </label>
                    </div>
                    <span v-else class="qa-hint">
                      Add destinations in the Destinations tab first.
                    </span>
                  </label>
                </div>
              </details>
            </div>
          </section>
        </div>

        <div v-show="isTab('connections')" class="qa-tab-panel">
          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">Bot connections</h2>
            </div>
            <div class="qa-card-body">
              <!-- Customers arrived here holding no token and no idea where to get
                   one, and the two things that most often stop a first setup —
                   the bot not being in the group, and the hub switch being off —
                   are both invisible from this tab. Folded by default so it costs
                   nothing once you have done it. -->
              <!-- prettier-ignore -->
              <details class="qa-details ah-setup">
                <summary>
                  First-time bot setup — 7 steps, about 5 minutes
                </summary>
                <div class="ah-setup-body">
                  <ol class="ah-guide-steps">
                    <li>
                      <span class="ah-step-title">Open Telegram and find BotFather</span>
                      <p>
                      Search for <code>@BotFather</code> and open the chat — Telegram's own bot for
                      making bots, the account with the blue verified tick. Press Start if you have
                      never used it.
                    </p>
                    </li>
                    <li>
                      <span class="ah-step-title">Create the bot</span>
                      <p>Send <code>/newbot</code> and answer the two questions it asks.</p>
                      <div class="ah-chat">
                        <span class="ah-chat-you">/newbot</span>
                        <span class="ah-chat-bot">BotFather:</span> Alright, a new bot. How are we
                        going to call it?
                        <span class="ah-chat-you">Acme QA Alerts</span>
                        <span class="ah-chat-note">&#8592; any display name</span>
                        <span class="ah-chat-bot">BotFather:</span> Good. Now let's choose a username
                        for your bot.
                        <span class="ah-chat-you">acme_qa_alerts_bot</span>
                        <span class="ah-chat-note">&#8592; must be unique and end in bot</span>
                      </div>
                      <p>BotFather replies with a token that looks like this:</p>
                      <div class="ah-chat ah-chat--plain">
                        8123456789:AAF3k9QmZ2pLxR7vN0sT1uW4yB6cD8eF0gH
                      </div>
                      <span class="ah-setup-warn">
                        Treat the token like a password — anyone holding it can post as your bot.
                        Never paste it into a group chat or a support ticket.
                      </span>

                    </li>
                    <li>
                      <span class="ah-step-title">Add the connection here</span>
                      <p>Fill the three fields on this tab and press Add connection.</p>
                      <div class="qa-table-wrap">
                        <table class="qa-table">
                          <thead>
                            <tr>
                              <th class="qa-th">Field</th>
                              <th class="qa-th">What to put</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td class="qa-td"><b>Name</b></td>
                              <td class="qa-td">
                                Anything you will recognise later, e.g. <em>Acme QA Alerts</em>. It is
                                only a label.
                              </td>
                            </tr>
                            <tr>
                              <td class="qa-td"><b>Source</b></td>
                              <td class="qa-td">
                                Leave it on <b>Custom token</b> — the option for a bot you made
                                yourself.
                              </td>
                            </tr>
                            <tr>
                              <td class="qa-td"><b>Bot token</b></td>
                              <td class="qa-td">Paste the token from step 2.</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p>
                        The connection appears below with a green Connected mark. If it does not, the
                        token is wrong — copy it again from BotFather.
                      </p>
                    </li>
                    <li>
                      <span class="ah-step-title">Add the bot to your Telegram group</span>
                      <p>
                      Open the group that should receive the alerts, add your bot by its username,
                      then promote it to administrator in the group settings.
                      </p>
                      <span class="ah-setup-warn">
                        This is the step that makes your group appear in the next tab. Telegram never
                        tells a bot which groups exist; it reports a group only at the moment the bot
                        is added or promoted. Skip this and the Destinations list stays empty no
                        matter what else you do.
                      </span>

                    </li>
                    <li>
                      <span class="ah-step-title">Point the alerts at the group</span>
                      <p>
                      Go to Destinations, choose your group in the picker, press Connect, then
                      Save Hub Settings at the top of the page. Nothing is stored until you save.
                    </p>
                    </li>
                    <li>
                      <span class="ah-step-title">Turn the hub on</span>
                      <p>
                      Go to Settings, set Alert Hub enabled to Enabled, and save again.
                      </p>
                      <span class="ah-setup-warn">
                        Everything above can be correct and still send nothing while this is off — it
                        is the first thing checked, before the token, the group and the rules. A test
                        then comes back as <code>Test skipped: alert_hub_disabled</code>.
                      </span>

                    </li>
                    <li>
                      <span class="ah-step-title">Send a test</span>
                      <p>
                      On the Delivery tab, pick your group in the Destination field first, then press
                      Send test. Choosing it explicitly sends straight to that group and takes the
                      routing rules out of the picture, so this really is a test of the bot. The
                      message should arrive within seconds and the page should report
                      <code>delivered=1</code>. Real alerts then follow the rules on Routing.
                    </p>
                    </li>
                  </ol>

                  <h4 class="ah-setup-head">If the test says “skipped”</h4>
                  <p class="qa-hint">
                    The page gives a reason, but as an internal code. These are all of them.
                  </p>
                  <div class="qa-table-wrap">
                    <table class="qa-table">
                      <thead>
                        <tr>
                          <th class="qa-th">Reason</th>
                          <th class="qa-th">What it means and what to do</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td class="qa-td"><code>alert_hub_disabled</code></td>
                          <td class="qa-td">
                            The master switch is off. Settings → Alert Hub enabled → Enabled → save.
                            That is step 6 above.
                          </td>
                        </tr>
                        <tr>
                          <td class="qa-td"><code>missing_bot_token</code></td>
                          <td class="qa-td">
                            No usable bot token. Check the connection on this tab shows Connected; if
                            not, press Re-check or paste the token again.
                          </td>
                        </tr>
                        <tr>
                          <td class="qa-td"><code>no_destination_match</code></td>
                          <td class="qa-td">
                            Nothing to send to. Pick your group in the Destination field before
                            sending, and make sure it is connected on Destinations and saved.
                          </td>
                        </tr>
                        <tr>
                          <td class="qa-td"><code>shadow_mode</code></td>
                          <td class="qa-td">
                            The hub is in observe-only mode for this account — it decides what it
                            would send but sends nothing. An environment setting; ask an
                            administrator.
                          </td>
                        </tr>
                        <tr>
                          <td class="qa-td"><code>rollout_blocked</code></td>
                          <td class="qa-td">
                            Delivery is switched off for this account at the environment level. Also
                            an administrator setting, not something on this page.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h4 class="ah-setup-head">The group is not in the Destinations list</h4>
                  <p class="qa-desc">
                    Almost always the bot was added to the group before the connection existed here,
                    so Telegram had nobody to tell. Post any message in the group — that reports it
                    too. If it still does not appear, remove the bot from the group, add it again,
                    then press Re-check on the connection.
                  </p>

                  <h4 class="ah-setup-head">The other two Source options</h4>
                  <p class="qa-desc">
                    You will not need these unless someone set them up for you.
                    <em>Telegram internal</em> reuses a bot token already saved in this account's own
                    Telegram settings — pick it only if an administrator has configured one, and leave
                    the token field empty. <em>Inbox channel</em> reuses the bot behind an existing
                    Telegram inbox, so alerts and customer chats share one bot; it is only useful if
                    this account already has a Telegram inbox.
                  </p>
                </div>
              </details>

              <div
                class="qa-form-grid qa-form-grid--3"
                style="margin-bottom: 12px"
              >
                <label class="qa-field">
                  <span class="qa-label">Name</span>
                  <input v-model="connectionForm.name" class="qa-input" />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Source</span>
                  <select v-model="connectionForm.source" class="qa-select">
                    <option value="custom">Custom token</option>
                    <option value="telegram_internal">Telegram internal</option>
                    <option value="inbox_channel">Inbox channel</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Bot token</span>
                  <input
                    v-model="connectionForm.botToken"
                    class="qa-input"
                    type="password"
                  />
                </label>
              </div>
              <button
                type="button"
                class="qa-btn qa-btn--primary"
                :disabled="isConnectionSaving"
                @click="createConnection"
              >
                {{
                  isConnectionSaving
                    ? t('QA_DASHBOARD.ALERT_HUB.BTN_SAVING')
                    : t('QA_DASHBOARD.ALERT_HUB.BTN_ADD_CONNECTION')
                }}
              </button>
              <div class="qa-table-wrap" style="margin-top: 16px">
                <div class="ah-conns">
                  <div
                    v-for="item in connections"
                    :key="`conn-${item.id}`"
                    class="ah-conn"
                    :class="`ah-conn--${connStatus(item)}`"
                  >
                    <!-- prettier-ignore -->
                    <div class="ah-avatar" :class="`ah-avatar--${connStatus(item)}`">🤖</div>
                    <div class="ah-body">
                      <div class="ah-name">
                        {{ item.name }}
                        <span
                          v-if="connStatus(item) === 'connected'"
                          class="qa-badge qa-badge--good"
                          >● Connected</span
                        >
                        <span
                          v-else-if="connStatus(item) === 'error'"
                          class="qa-badge qa-badge--bad"
                          >● Connection failed</span
                        >
                        <span v-else class="qa-badge qa-badge--warn"
                          >● Not checked yet</span
                        >
                      </div>
                      <div v-if="botHandle(item)" class="ah-id">
                        <b>{{ botHandle(item) }}</b
                        ><span v-if="botName(item)">
                          · {{ botName(item) }}</span
                        >
                      </div>
                      <div
                        v-else-if="connStatus(item) !== 'error'"
                        class="ah-id"
                      >
                        Press <b>Verify</b> to confirm the bot &amp; the
                        channels it can reach.
                      </div>
                      <div v-if="connStatus(item) === 'error'" class="ah-err">
                        {{ item.last_error }}
                      </div>
                      <div class="ah-sub">
                        <span
                          v-if="reachSummary(item)"
                          :class="
                            reachSummary(item).allOk
                              ? 'ah-reach-ok'
                              : 'ah-reach-warn'
                          "
                        >
                          {{ reachSummary(item).allOk ? '✓' : '⚠' }}
                          reaches {{ reachSummary(item).ok }}/{{
                            reachSummary(item).total
                          }}
                          channels
                        </span>
                        <span class="ah-token"
                          ><code>{{ item.masked_token || '-' }}</code></span
                        >
                        <span v-if="item.last_verified_at" class="ah-when">
                          Checked
                          {{ new Date(item.last_verified_at).toLocaleString() }}
                        </span>
                      </div>
                    </div>
                    <div class="ah-actions">
                      <button
                        type="button"
                        class="qa-btn qa-btn--sm qa-btn--primary"
                        @click="verifyConnection(item.id)"
                      >
                        {{
                          connStatus(item) === 'unverified'
                            ? 'Verify'
                            : 'Re-check'
                        }}
                      </button>
                      <button
                        type="button"
                        class="qa-btn qa-btn--ghost qa-btn--sm"
                        @click="deleteConnection(item.id)"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div v-if="!connections.length" class="qa-card">
                    <div class="qa-card-body">
                      <p class="qa-desc">
                        No bot connections yet. Add one above to start sending
                        QA alerts to Telegram.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section v-if="relevantDiscoveredGroups.length" class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                  />
                </svg>
                Discovered Telegram Groups
              </h2>
            </div>
            <div class="qa-table-wrap">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th">Title</th>
                    <th class="qa-th">Chat ID</th>
                    <th class="qa-th">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="group in relevantDiscoveredGroups" :key="group.id">
                    <td class="qa-td">
                      {{ group.title || group.username || `#${group.id}` }}
                    </td>
                    <td class="qa-td">{{ group.id }}</td>
                    <td class="qa-td">
                      {{
                        group.last_seen_at
                          ? new Date(group.last_seen_at).toLocaleString()
                          : '-'
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div v-show="isTab('destinations')" class="qa-tab-panel">
          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.TITLE') }}
              </h2>
              <button
                type="button"
                class="qa-btn qa-btn--ghost qa-btn--sm"
                data-qa-marker="refresh-groups"
                :disabled="isHubLoading"
                @click="loadHub"
              >
                {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.REFRESH') }}
              </button>
            </div>

            <div class="qa-card-body">
              <p v-if="activeConnection" class="qa-hint">
                {{
                  t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.SENDING_BOT', {
                    bot: activeBotHandle || activeConnection.name,
                  })
                }}
              </p>
              <p v-else class="ah-dest-warn" data-qa-marker="no-connection">
                {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.NO_CONNECTION') }}
              </p>

              <div
                v-if="!hubForm.destinations.length"
                class="ah-empty"
                data-qa-marker="destinations-empty"
              >
                <p class="ah-empty-title">
                  {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.EMPTY_TITLE') }}
                </p>
                <p class="qa-hint">
                  {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.EMPTY_BODY') }}
                </p>
              </div>

              <div v-else class="ah-dests">
                <article
                  v-for="destination in hubForm.destinations"
                  :key="destination.id"
                  class="ah-dest"
                  :data-qa-destination="destination.id"
                >
                  <div class="ah-dest-main">
                    <div class="ah-dest-head">
                      <span :class="destinationBadgeClass(destination)">
                        {{ destinationStateLabel(destination) }}
                      </span>
                      <strong class="ah-dest-title">
                        {{
                          destination.name ||
                          destination.chatId ||
                          destination.id
                        }}
                      </strong>
                      <span class="ah-dest-type">{{
                        destination.destinationType
                      }}</span>
                    </div>
                    <div class="ah-dest-meta">
                      <span>
                        {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.CHAT_ID') }}:
                        <code>{{ destination.chatId || '—' }}</code>
                      </span>
                      <span v-if="destination.messageThreadId">
                        {{
                          t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.THREAD_ID')
                        }}:
                        <code>{{ destination.messageThreadId }}</code>
                      </span>
                    </div>
                    <p
                      v-if="destinationStateDetail(destination)"
                      class="qa-hint"
                    >
                      {{ destinationStateDetail(destination) }}
                    </p>
                    <p
                      v-if="!isDestinationSaved(destination)"
                      class="ah-dest-warn"
                    >
                      {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.UNSAVED') }}
                    </p>
                  </div>

                  <div class="ah-dest-actions">
                    <button
                      type="button"
                      class="qa-btn qa-btn--sm qa-btn--primary"
                      :disabled="!canTestDestination(destination)"
                      :title="
                        isDestinationSaved(destination)
                          ? ''
                          : t(
                              'QA_DASHBOARD.ALERT_HUB.DESTINATIONS.TEST_NEEDS_SAVE'
                            )
                      "
                      @click="sendDestinationTest(destination)"
                    >
                      {{
                        testingDestinationId === destination.id
                          ? t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.TESTING')
                          : t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.TEST')
                      }}
                    </button>
                    <button
                      type="button"
                      class="qa-btn qa-btn--ghost qa-btn--sm"
                      @click="toggleDestinationEdit(destination.id)"
                    >
                      {{
                        editingDestinationId === destination.id
                          ? t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.EDIT_DONE')
                          : t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.EDIT')
                      }}
                    </button>
                    <button
                      type="button"
                      class="qa-btn qa-btn--danger qa-btn--sm"
                      @click="removeDestination(destination.id)"
                    >
                      {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.REMOVE') }}
                    </button>
                  </div>

                  <div
                    v-if="editingDestinationId === destination.id"
                    class="ah-dest-edit"
                  >
                    <label class="ah-field">
                      <span>{{
                        t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.NAME')
                      }}</span>
                      <input v-model="destination.name" class="qa-input" />
                    </label>
                    <label class="ah-field">
                      <span>{{
                        t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.CHAT_ID')
                      }}</span>
                      <input
                        v-model="destination.chatId"
                        class="qa-input"
                        :placeholder="
                          t(
                            'QA_DASHBOARD.ALERT_HUB.DESTINATIONS.CHAT_ID_PLACEHOLDER'
                          )
                        "
                      />
                    </label>
                    <label class="ah-field">
                      <span>{{
                        t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.TYPE')
                      }}</span>
                      <select
                        v-model="destination.destinationType"
                        class="qa-select"
                      >
                        <option value="group">group</option>
                        <option value="channel">channel</option>
                        <option value="topic">topic</option>
                        <option value="thread">thread</option>
                      </select>
                    </label>
                    <label class="ah-field">
                      <span>{{
                        t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.THREAD_ID')
                      }}</span>
                      <input
                        v-model="destination.messageThreadId"
                        class="qa-input"
                      />
                    </label>
                    <label class="ah-field ah-field--check">
                      <input v-model="destination.enabled" type="checkbox" />
                      <span>{{
                        t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.ENABLED')
                      }}</span>
                    </label>
                  </div>
                </article>
              </div>

              <div v-if="activeConnection" class="ah-connect">
                <template v-if="connectableGroups.length">
                  <select
                    v-model="groupToConnect"
                    class="qa-select"
                    data-qa-marker="group-to-connect"
                  >
                    <option value="">
                      {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.PICK_GROUP') }}
                    </option>
                    <option
                      v-for="group in connectableGroups"
                      :key="`cg-${group.id}`"
                      :value="group.id"
                    >
                      {{ groupLabel(group) }}
                    </option>
                  </select>
                  <button
                    type="button"
                    class="qa-btn qa-btn--primary"
                    data-qa-marker="connect-group"
                    :disabled="!groupToConnect"
                    @click="connectGroup(groupToConnect)"
                  >
                    {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.CONNECT') }}
                  </button>
                </template>
                <button
                  type="button"
                  class="qa-btn qa-btn--ghost"
                  data-qa-marker="add-destination"
                  @click="addDestination"
                >
                  {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.ADD_MANUAL') }}
                </button>
              </div>

              <!-- Discovery only learns a group from an update, so "nothing found" must
               never be phrased as "the bot is in no group" while a destination is
               plainly delivering: groups joined before the webhook existed are
               invisible to it. -->
              <div
                v-if="showGroupHowto"
                class="ah-howto"
                data-qa-marker="group-howto"
              >
                <p class="ah-howto-title">
                  {{
                    t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.NO_GROUPS_FOUND', {
                      bot: activeBotHandle,
                    })
                  }}
                </p>
                <ol class="ah-steps">
                  <li>
                    {{
                      t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.STEP_INVITE', {
                        bot: activeBotHandle,
                      })
                    }}
                  </li>
                  <li>
                    {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.STEP_PROMOTE') }}
                  </li>
                  <li>
                    {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.STEP_REFRESH') }}
                  </li>
                </ol>
              </div>
              <p
                v-else-if="showNoNewGroups"
                class="qa-hint"
                data-qa-marker="no-new-groups"
              >
                {{
                  t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.NO_NEW_GROUPS', {
                    bot: activeBotHandle,
                  })
                }}
              </p>

              <p class="qa-hint">
                {{ t('QA_DASHBOARD.ALERT_HUB.DESTINATIONS.SAVE_NOTE') }}
              </p>
            </div>
          </section>
        </div>

        <div v-show="isTab('routing')" class="qa-tab-panel">
          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  />
                </svg>
                {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.TITLE') }}
              </h2>
              <button type="button" class="qa-btn" @click="addRoute()">
                {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.ADD_RULE') }}
              </button>
            </div>
            <div class="qa-card-body">
              <p class="qa-hint" style="margin: 0">
                {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.SUBTITLE') }}
              </p>

              <div v-if="routingGaps.length" class="ah-gap">
                <div class="ah-gap-title">
                  {{
                    t('QA_DASHBOARD.ALERT_HUB.ROUTING.GAP_TITLE', {
                      count: routingGapTotal,
                    })
                  }}
                </div>
                <div
                  v-for="gap in routingGaps"
                  :key="`gap-${gap.key}`"
                  class="ah-gap-row"
                >
                  <span>
                    {{
                      t('QA_DASHBOARD.ALERT_HUB.ROUTING.GAP_ITEM', {
                        key: typeLabel(gap.key),
                        count: gap.count,
                      })
                    }}
                  </span>
                  <button
                    type="button"
                    class="qa-btn qa-btn--sm"
                    @click="addRoute(gap.key)"
                  >
                    {{
                      t('QA_DASHBOARD.ALERT_HUB.ROUTING.GAP_ACTION', {
                        key: typeLabel(gap.key),
                      })
                    }}
                  </button>
                </div>
                <p
                  v-if="routeCoverage.observed_since"
                  class="qa-hint"
                  style="margin: 8px 0 0"
                >
                  {{
                    t('QA_DASHBOARD.ALERT_HUB.ROUTING.COVERAGE_SINCE', {
                      at: new Date(
                        routeCoverage.observed_since
                      ).toLocaleString(),
                    })
                  }}
                </p>
              </div>

              <div v-if="!hubForm.routes.length" class="ah-empty">
                <div class="ah-empty-title">
                  {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.EMPTY_TITLE') }}
                </div>
                <p class="qa-hint" style="margin: 6px 0 0">
                  {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.EMPTY_BODY') }}
                </p>
              </div>

              <div class="qa-route-list" style="margin-top: 14px">
                <div
                  v-for="(route, routeIndex) in hubForm.routes"
                  :key="route.id"
                  class="qa-route-card"
                  :data-qa-route="route.id"
                >
                  <div class="qa-route-card-header">
                    <div class="qa-route-card-meta">
                      <span class="ah-rule-title">
                        {{ typeLabel(route.incidentType) }}
                      </span>
                      <span class="qa-badge qa-badge--muted">
                        {{
                          t('QA_DASHBOARD.ALERT_HUB.ROUTING.RULE_LABEL', {
                            index: routeIndex + 1,
                          })
                        }}
                      </span>
                      <label class="qa-route-enabled">
                        <input v-model="route.enabled" type="checkbox" />
                        <span>{{
                          t('QA_DASHBOARD.ALERT_HUB.ROUTING.ENABLED')
                        }}</span>
                      </label>
                    </div>
                    <div class="qa-actions qa-actions--compact">
                      <button
                        type="button"
                        class="qa-btn qa-btn--ghost qa-btn--sm"
                        :disabled="previewOf(route)?.loading"
                        @click="previewRoute(route)"
                      >
                        {{
                          previewOf(route)?.loading
                            ? t(
                                'QA_DASHBOARD.ALERT_HUB.ROUTING.PREVIEW_RUNNING'
                              )
                            : t('QA_DASHBOARD.ALERT_HUB.ROUTING.PREVIEW')
                        }}
                      </button>
                      <button
                        type="button"
                        class="qa-btn qa-btn--ghost qa-btn--sm"
                        @click="removeRoute(route.id)"
                      >
                        {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.DELETE') }}
                      </button>
                    </div>
                  </div>

                  <div class="qa-route-card-body">
                    <div v-if="routeWarnings(route).length" class="ah-warns">
                      <div class="ah-warns-title">
                        {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.WARN_TITLE') }}
                      </div>
                      <ul class="ah-warns-list">
                        <li
                          v-for="(warning, warnIndex) in routeWarnings(route)"
                          :key="`warn-${route.id}-${warnIndex}`"
                        >
                          {{
                            t(
                              `QA_DASHBOARD.ALERT_HUB.ROUTING.${warning.key}`,
                              warning.params || {}
                            )
                          }}
                        </li>
                      </ul>
                    </div>

                    <div class="qa-form-grid qa-form-grid--2">
                      <label class="qa-field">
                        <span class="qa-label">
                          {{
                            t('QA_DASHBOARD.ALERT_HUB.ROUTING.INCIDENT_TYPE')
                          }}
                        </span>
                        <select v-model="route.incidentType" class="qa-select">
                          <option value="*">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.ANY_TYPE') }}
                          </option>
                          <optgroup
                            v-for="group in typeGroups"
                            :key="`group-${route.id}-${group.group}`"
                            :label="group.label"
                          >
                            <option
                              v-for="item in group.items"
                              :key="`type-${route.id}-${item.key}`"
                              :value="item.key"
                            >
                              {{ typeLabel(item.key) }}
                            </option>
                          </optgroup>
                          <option
                            v-if="
                              route.incidentType &&
                              !typeMeta(route.incidentType)
                            "
                            :value="route.incidentType"
                          >
                            {{ typeLabel(route.incidentType) }}
                          </option>
                        </select>
                        <span class="qa-hint">
                          {{
                            t(
                              'QA_DASHBOARD.ALERT_HUB.ROUTING.INCIDENT_TYPE_HINT'
                            )
                          }}
                        </span>
                      </label>
                      <label class="qa-field">
                        <span class="qa-label">
                          {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.SEND_TO') }}
                        </span>
                        <select
                          v-model="route.destinationIds"
                          class="qa-select qa-multi-select"
                          multiple
                        >
                          <option
                            v-for="item in hubForm.destinations"
                            :key="`dest-${route.id}-${item.id}`"
                            :value="String(item.id)"
                          >
                            {{ item.name || item.chatId }}
                          </option>
                        </select>
                        <span class="qa-hint">
                          {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.SEND_TO_HINT') }}
                        </span>
                      </label>
                    </div>

                    <div class="ah-cover">
                      <span class="qa-label" style="margin: 0">
                        {{
                          t('QA_DASHBOARD.ALERT_HUB.ROUTING.COVERAGE_TITLE', {
                            days: routeCoverage.window_days || 7,
                          })
                        }}
                      </span>
                      <span v-if="coverageFor(route)" class="ah-cover-line">
                        {{
                          t('QA_DASHBOARD.ALERT_HUB.ROUTING.COVERAGE_LINE', {
                            total: coverageFor(route).total,
                            delivered: coverageFor(route).delivered,
                            skipped: coverageFor(route).skipped,
                          })
                        }}
                      </span>
                      <span v-else class="qa-hint">
                        {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.COVERAGE_NONE') }}
                      </span>
                      <span
                        v-if="deliveriesFor(route)"
                        class="qa-badge qa-badge--good"
                      >
                        {{ deliveriesFor(route) }}
                      </span>
                      <span
                        v-for="entry in skipReasonsFor(route)"
                        :key="`reason-${route.id}-${entry.reason}`"
                        class="ah-cover-reason"
                      >
                        {{
                          t('QA_DASHBOARD.ALERT_HUB.ROUTING.COVERAGE_REASON', {
                            count: entry.count,
                            reason: entry.label,
                          })
                        }}
                      </span>
                    </div>

                    <div v-if="previewOf(route)?.result" class="ah-preview">
                      <div>
                        {{
                          previewOf(route).result.covered
                            ? t(
                                'QA_DASHBOARD.ALERT_HUB.ROUTING.PREVIEW_RESULT',
                                {
                                  covered: previewOf(route).result.covered,
                                  considered:
                                    previewOf(route).result.considered,
                                }
                              )
                            : t('QA_DASHBOARD.ALERT_HUB.ROUTING.PREVIEW_NONE')
                        }}
                      </div>
                      <p
                        v-if="previewOf(route).result.unrecorded"
                        class="qa-hint"
                        style="margin: 4px 0 0"
                      >
                        {{
                          t(
                            'QA_DASHBOARD.ALERT_HUB.ROUTING.PREVIEW_UNRECORDED',
                            {
                              count: previewOf(route).result.unrecorded,
                            }
                          )
                        }}
                      </p>
                      <p class="qa-hint" style="margin: 4px 0 0">
                        {{
                          t(
                            'QA_DASHBOARD.ALERT_HUB.ROUTING.PREVIEW_COOLDOWN_NOTE'
                          )
                        }}
                      </p>
                    </div>

                    <div
                      v-if="statusOptionsFor(route).length"
                      class="ah-opt-block"
                    >
                      <span class="qa-label">
                        {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.STATUSES') }}
                      </span>
                      <div class="ah-opts">
                        <label
                          v-for="status in statusOptionsFor(route)"
                          :key="`status-${route.id}-${status}`"
                          class="ah-opt"
                        >
                          <input
                            type="checkbox"
                            :checked="route.statuses.includes(status)"
                            @change="toggleInArray(route.statuses, status)"
                          />
                          <span>{{ statusLabel(status) }}</span>
                        </label>
                      </div>
                      <span class="qa-hint">
                        {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.ANY_VALUE') }}
                      </span>
                    </div>

                    <div class="ah-opt-block">
                      <span class="qa-label">
                        {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.SEVERITIES') }}
                      </span>
                      <div class="ah-opts">
                        <label
                          v-for="severity in severityOptionsFor(route)"
                          :key="`severity-${route.id}-${severity}`"
                          class="ah-opt"
                        >
                          <input
                            type="checkbox"
                            :checked="route.severities.includes(severity)"
                            @change="toggleInArray(route.severities, severity)"
                          />
                          <span>{{ severityLabel(severity) }}</span>
                        </label>
                      </div>
                    </div>

                    <details class="qa-details">
                      <summary>
                        {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.NARROW_TITLE') }}
                      </summary>
                      <p class="qa-hint" style="margin: 6px 0 10px">
                        {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.NARROW_HINT') }}
                      </p>
                      <div class="qa-form-grid">
                        <label
                          v-if="filterAllowed(route, 'inbox')"
                          class="qa-field"
                        >
                          <span class="qa-label">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.INBOXES') }}
                          </span>
                          <select
                            v-model="route.inboxIds"
                            class="qa-select qa-multi-select"
                            multiple
                          >
                            <option
                              v-for="item in routingOptions.inboxes"
                              :key="`inbox-${route.id}-${item.id}`"
                              :value="String(item.id)"
                            >
                              {{ item.name }}
                            </option>
                          </select>
                        </label>
                        <label
                          v-if="filterAllowed(route, 'team')"
                          class="qa-field"
                        >
                          <span class="qa-label">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.TEAMS') }}
                          </span>
                          <select
                            v-model="route.teamIds"
                            class="qa-select qa-multi-select"
                            multiple
                          >
                            <option
                              v-for="item in routingOptions.teams"
                              :key="`team-${route.id}-${item.id}`"
                              :value="String(item.id)"
                            >
                              {{ item.name }}
                            </option>
                          </select>
                        </label>
                        <label
                          v-if="filterAllowed(route, 'agent')"
                          class="qa-field"
                        >
                          <span class="qa-label">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.AGENTS') }}
                          </span>
                          <select
                            v-model="route.userIds"
                            class="qa-select qa-multi-select"
                            multiple
                          >
                            <option
                              v-for="item in routingOptions.agents"
                              :key="`agent-${route.id}-${item.id}`"
                              :value="String(item.id)"
                            >
                              {{ item.name }}
                            </option>
                          </select>
                        </label>
                        <label
                          v-if="filterAllowed(route, 'channel')"
                          class="qa-field"
                        >
                          <span class="qa-label">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.CHANNELS') }}
                          </span>
                          <select
                            v-model="route.channelTypes"
                            class="qa-select qa-multi-select"
                            multiple
                          >
                            <option
                              v-for="item in routingOptions.channelTypes"
                              :key="`channel-${route.id}-${item}`"
                              :value="item"
                            >
                              {{ item }}
                            </option>
                          </select>
                        </label>
                      </div>

                      <div class="ah-opt-block">
                        <div class="ah-sched-head">
                          <span class="qa-label" style="margin: 0">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.WEEKDAYS') }}
                          </span>
                          <select
                            class="qa-select qa-btn--sm"
                            style="min-width: 170px"
                            @change="
                              applyRoutePreset(route, $event.target.value);
                              $event.target.value = '';
                            "
                          >
                            <option value="">
                              {{
                                t(
                                  'QA_DASHBOARD.ALERT_HUB.ROUTING.SCHEDULE_PRESET'
                                )
                              }}
                            </option>
                            <option value="weekdays_office">
                              {{
                                t(
                                  'QA_DASHBOARD.ALERT_HUB.ROUTING.PRESET_OFFICE'
                                )
                              }}
                            </option>
                            <option value="night_shift">
                              {{
                                t('QA_DASHBOARD.ALERT_HUB.ROUTING.PRESET_NIGHT')
                              }}
                            </option>
                            <option value="all_day">
                              {{
                                t('QA_DASHBOARD.ALERT_HUB.ROUTING.PRESET_ALL')
                              }}
                            </option>
                          </select>
                        </div>
                        <div class="ah-opts">
                          <label
                            v-for="day in [0, 1, 2, 3, 4, 5, 6]"
                            :key="`day-${route.id}-${day}`"
                            class="ah-opt"
                          >
                            <input
                              type="checkbox"
                              :checked="route.weekdays.includes(day)"
                              @change="toggleInArray(route.weekdays, day)"
                            />
                            <span>{{
                              t(`QA_DASHBOARD.ALERT_HUB.ROUTING.DAY_${day}`)
                            }}</span>
                          </label>
                        </div>
                      </div>

                      <label class="qa-field" style="margin-top: 12px">
                        <span class="qa-label">
                          {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.TIME_WINDOWS') }}
                        </span>
                        <input
                          v-model="route.timeWindowsText"
                          class="qa-input"
                          :placeholder="
                            t(
                              'QA_DASHBOARD.ALERT_HUB.ROUTING.TIME_WINDOWS_PLACEHOLDER'
                            )
                          "
                        />
                        <span v-if="!timeWindowsValid(route)" class="qa-error">
                          {{
                            t(
                              'QA_DASHBOARD.ALERT_HUB.ROUTING.TIME_WINDOWS_INVALID'
                            )
                          }}
                        </span>
                      </label>

                      <div
                        class="qa-form-grid qa-form-grid--2"
                        style="margin-top: 12px"
                      >
                        <label class="qa-field">
                          <span class="qa-label">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.COOLDOWN') }}
                            ({{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.SECONDS') }})
                          </span>
                          <input
                            v-model.number="route.cooldownSeconds"
                            class="qa-input"
                            type="number"
                            min="0"
                          />
                          <span class="qa-hint">
                            {{
                              t('QA_DASHBOARD.ALERT_HUB.ROUTING.COOLDOWN_HINT')
                            }}
                          </span>
                        </label>
                        <label class="qa-field">
                          <span class="qa-label">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.DIGEST') }}
                            ({{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.SECONDS') }})
                          </span>
                          <input
                            v-model.number="route.digestWindowSeconds"
                            class="qa-input"
                            type="number"
                            min="0"
                          />
                          <span class="qa-hint">
                            {{
                              t('QA_DASHBOARD.ALERT_HUB.ROUTING.DIGEST_HINT')
                            }}
                          </span>
                        </label>
                        <label class="qa-field">
                          <span class="qa-label">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.PRIORITY') }}
                          </span>
                          <input
                            v-model.number="route.priority"
                            class="qa-input"
                            type="number"
                          />
                          <span class="qa-hint">
                            {{
                              t('QA_DASHBOARD.ALERT_HUB.ROUTING.PRIORITY_HINT')
                            }}
                          </span>
                        </label>
                        <div class="qa-field">
                          <label class="ah-opt" style="width: fit-content">
                            <input
                              v-model="route.stopProcessing"
                              type="checkbox"
                            />
                            <span>{{
                              t('QA_DASHBOARD.ALERT_HUB.ROUTING.STOP')
                            }}</span>
                          </label>
                          <span class="qa-hint">
                            {{ t('QA_DASHBOARD.ALERT_HUB.ROUTING.STOP_HINT') }}
                          </span>
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div v-show="isTab('delivery')" class="qa-tab-panel">
          <div class="qa-toolbar">
            <div class="qa-toolbar-left">
              <label class="qa-field" style="min-width: 130px">
                <span class="qa-label">Status</span>
                <select
                  v-model="logFilters.status"
                  class="qa-select"
                  @change="onLogFilterChange"
                >
                  <option value="all">All</option>
                  <option value="delivered">Delivered</option>
                  <option value="failed">Failed</option>
                  <option value="skipped">Skipped</option>
                </select>
              </label>
              <label class="qa-field" style="min-width: 160px">
                <span class="qa-label">Incident type</span>
                <select
                  v-model="logFilters.incidentType"
                  class="qa-select"
                  @change="onLogFilterChange"
                >
                  <option value="">All types</option>
                  <optgroup
                    v-for="group in typeGroups"
                    :key="`logf-grp-${group.group}`"
                    :label="group.label"
                  >
                    <option
                      v-for="item in group.items"
                      :key="`logf-type-${item.key}`"
                      :value="item.key"
                    >
                      {{ typeLabel(item.key) }}
                    </option>
                  </optgroup>
                </select>
              </label>
              <label class="qa-field" style="min-width: 140px">
                <span class="qa-label">Destination</span>
                <input
                  v-model="logFilters.destinationId"
                  class="qa-input"
                  placeholder="Destination ID"
                  @keydown.enter="onLogFilterChange"
                />
              </label>
              <label class="qa-field" style="min-width: 180px">
                <span class="qa-label">Route</span>
                <select
                  v-model="logFilters.routeId"
                  class="qa-select"
                  @change="onLogFilterChange"
                >
                  <option value="all">All</option>
                  <option
                    v-for="item in routeFilterOptions"
                    :key="`route-filter-${item.id}`"
                    :value="item.id"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </label>
              <label class="qa-field" style="min-width: 180px; flex: 1">
                <span class="qa-label">Search</span>
                <input
                  v-model="logFilters.query"
                  class="qa-input"
                  placeholder="event_key / error"
                  @keydown.enter="onLogFilterChange"
                />
              </label>
            </div>
            <div class="qa-toolbar-right">
              <button
                type="button"
                class="qa-btn"
                :disabled="isLogsLoading"
                @click="loadLogs"
              >
                {{
                  isLogsLoading
                    ? t('QA_DASHBOARD.ALERT_HUB.BTN_LOADING')
                    : t('QA_DASHBOARD.ALERT_HUB.BTN_REFRESH_LOGS')
                }}
              </button>
            </div>
          </div>

          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">Route simulator</h2>
              <button
                type="button"
                class="qa-btn"
                :disabled="isSimulating"
                @click="runSimulate"
              >
                {{ isSimulating ? 'Simulating...' : 'Simulate' }}
              </button>
            </div>
            <div class="qa-card-body">
              <div class="qa-form-grid qa-form-grid--3">
                <label class="qa-field">
                  <span class="qa-label">Incident type</span>
                  <select
                    v-model="simulateForm.incidentType"
                    class="qa-select"
                    @change="resetStatusFor(simulateForm)"
                  >
                    <optgroup
                      v-for="group in typeGroups"
                      :key="`sim-grp-${group.group}`"
                      :label="group.label"
                    >
                      <option
                        v-for="item in group.items"
                        :key="`sim-type-${item.key}`"
                        :value="item.key"
                      >
                        {{ typeLabel(item.key) }}
                      </option>
                    </optgroup>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Status</span>
                  <select
                    v-model="simulateForm.incidentStatus"
                    class="qa-select"
                  >
                    <option
                      v-for="st in statusesForType(simulateForm.incidentType)"
                      :key="`sim-st-${st}`"
                      :value="st"
                    >
                      {{ prettyStatus(st) }}
                    </option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Severity</span>
                  <select v-model="simulateForm.severity" class="qa-select">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Inbox</span>
                  <select v-model="simulateForm.inboxId" class="qa-select">
                    <option value="">Any</option>
                    <option
                      v-for="item in routingOptions.inboxes"
                      :key="`sim-inbox-${item.id}`"
                      :value="String(item.id)"
                    >
                      {{ item.name }}
                    </option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Team</span>
                  <select v-model="simulateForm.teamId" class="qa-select">
                    <option value="">Any</option>
                    <option
                      v-for="item in routingOptions.teams"
                      :key="`sim-team-${item.id}`"
                      :value="String(item.id)"
                    >
                      {{ item.name }}
                    </option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Agent</span>
                  <select v-model="simulateForm.userId" class="qa-select">
                    <option value="">Any</option>
                    <option
                      v-for="item in routingOptions.agents"
                      :key="`sim-agent-${item.id}`"
                      :value="String(item.id)"
                    >
                      {{ item.name }}
                    </option>
                  </select>
                </label>
              </div>
              <div
                v-if="simulateResult"
                class="qa-sim-result"
                style="margin-top: 12px"
              >
                <p class="qa-hint">
                  Dry-run only — Telegram not sent (matched
                  {{ (simulateResult.matched_route_ids || []).length }},
                  rejected {{ (simulateResult.rejected || []).length }}).
                </p>
                <div
                  v-if="(simulateResult.matched_route_ids || []).length"
                  style="
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                    margin-top: 8px;
                  "
                >
                  <span
                    v-for="rid in simulateResult.matched_route_ids"
                    :key="`matched-${rid}`"
                    class="qa-pill qa-pill--good"
                  >
                    {{ routeLabelById(rid) }}
                  </span>
                </div>
                <div
                  v-if="(simulateResult.rejected || []).length"
                  class="qa-table-wrap"
                  style="margin-top: 8px"
                >
                  <table class="qa-table">
                    <thead>
                      <tr>
                        <th class="qa-th">Rejected route</th>
                        <th class="qa-th">Reasons</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in simulateResult.rejected"
                        :key="`rej-${row.id}`"
                      >
                        <td class="qa-td">
                          <code>{{ row.id }}</code>
                        </td>
                        <td class="qa-td">
                          {{ (row.reasons || []).join(', ') }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <details style="margin-top: 8px">
                  <!-- prettier-ignore -->
                  <summary class="qa-hint" style="cursor: pointer">Raw response</summary>
                  <pre
                    class="qa-hint"
                    style="margin-top: 6px; white-space: pre-wrap"
                    >{{ JSON.stringify(simulateResult, null, 2) }}</pre
                  >
                </details>
              </div>
            </div>
          </section>

          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
                Test Delivery
              </h2>
              <button
                type="button"
                class="qa-btn qa-btn--primary"
                :disabled="isTestSending"
                @click="sendTest"
              >
                {{ isTestSending ? 'Sending...' : 'Send Test' }}
              </button>
            </div>
            <div class="qa-card-body">
              <div class="qa-form-grid">
                <label class="qa-field">
                  <span class="qa-label">Destination</span>
                  <select v-model="testForm.destinationId" class="qa-select">
                    <option value="">Use route matching</option>
                    <option
                      v-for="opt in destinationOptions"
                      :key="opt.id"
                      :value="opt.id"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Incident type</span>
                  <select
                    v-model="testForm.incidentType"
                    class="qa-select"
                    @change="resetStatusFor(testForm)"
                  >
                    <optgroup
                      v-for="group in typeGroups"
                      :key="`test-grp-${group.group}`"
                      :label="group.label"
                    >
                      <option
                        v-for="item in group.items"
                        :key="`test-type-${item.key}`"
                        :value="item.key"
                      >
                        {{ typeLabel(item.key) }}
                      </option>
                    </optgroup>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Status</span>
                  <select v-model="testForm.incidentStatus" class="qa-select">
                    <option
                      v-for="st in statusesForType(testForm.incidentType)"
                      :key="`test-st-${st}`"
                      :value="st"
                    >
                      {{ prettyStatus(st) }}
                    </option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Severity</span>
                  <select v-model="testForm.severity" class="qa-select">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
                <label class="qa-field qa-field--span-2">
                  <span class="qa-label">Title</span>
                  <input v-model="testForm.title" class="qa-input" />
                </label>
                <label class="qa-field qa-field--span-2">
                  <span class="qa-label">Body</span>
                  <textarea
                    v-model="testForm.body"
                    class="qa-textarea"
                    rows="2"
                  />
                </label>
              </div>
            </div>
          </section>

          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
                Alert Logs
              </h2>
              <span class="qa-muted">
                Page {{ logFilters.page }} / {{ logMeta.totalPages }} ({{
                  logMeta.totalCount
                }}
                total)
              </span>
            </div>
            <div class="qa-table-wrap">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th">Time</th>
                    <th class="qa-th">Type</th>
                    <th class="qa-th">Status</th>
                    <th class="qa-th">Destination</th>
                    <th class="qa-th">Routing</th>
                    <th class="qa-th">Context</th>
                    <th class="qa-th">Event Key</th>
                    <th class="qa-th">Error</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isLogsLoading">
                    <td class="qa-td qa-td--empty" colspan="8">
                      <div class="qa-loading-row">
                        <div class="qa-spinner" />
                        <span>Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="log in logs" :key="log.id">
                    <td class="qa-td">
                      {{ new Date(log.created_at).toLocaleString() }}
                    </td>
                    <td class="qa-td">{{ log.incident_type }}</td>
                    <td class="qa-td">
                      <span :class="logStatusBadgeClass(log.status)">{{
                        log.status
                      }}</span>
                    </td>
                    <td class="qa-td">{{ log.destination_id }}</td>
                    <td class="qa-td qa-td--pre">
                      <div>{{ routingSummary(log) }}</div>
                      <div
                        v-if="routeIdsForLog(log).length"
                        class="qa-chip-row"
                        style="margin-top: 6px"
                      >
                        <button
                          v-for="routeId in routeIdsForLog(log)"
                          :key="`chip-${log.id}-${routeId}`"
                          type="button"
                          class="qa-chip"
                          @click="filterByRoute(routeId)"
                        >
                          {{ routeId }}
                        </button>
                      </div>
                    </td>
                    <td class="qa-td qa-td--pre">
                      {{ routingContextSummary(log) }}
                    </td>
                    <td class="qa-td">{{ log.event_key }}</td>
                    <td class="qa-td">{{ log.error_message || '-' }}</td>
                  </tr>
                  <tr v-if="!logs.length && !isLogsLoading">
                    <!-- prettier-ignore -->
                    <td class="qa-td qa-td--empty" colspan="8">No logs found.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="qa-pagination">
              <button
                type="button"
                class="qa-btn"
                :disabled="logFilters.page <= 1"
                @click="goToLogPage(logFilters.page - 1)"
              >
                Prev
              </button>
              <span class="qa-muted">
                Page {{ logFilters.page }} / {{ logMeta.totalPages }} ({{
                  logMeta.totalCount
                }}
                total)
              </span>
              <button
                type="button"
                class="qa-btn"
                :disabled="logFilters.page >= logMeta.totalPages"
                @click="goToLogPage(logFilters.page + 1)"
              >
                Next
              </button>
            </div>
          </section>
        </div>
      </div>

      <div class="qa-footer qa-footer--hidden">
        <span>
          Telegram Alert Hub — route workforce incidents to destinations and
          review delivery outcomes.
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '../styles/qaShell.css';

/* Every field here carries two edges: the 1px border qaShell draws, and a 1px
   outline _base.scss draws in --border-weak. On light the outline is 234 234
   234, faint enough against white that only qaShell's #d1d5db reads and the
   fields look uniform. Mix carries body.dark, so it picks up the DARK value
   38 38 42 — a near-black ring around a grey border, which is why the edges
   looked mismatched on Mix and matched everywhere else.
   _woot.scss already resets the token for Mix, but only inside
   .conversation-page-theme; QA pages were never in scope. Same value, same
   reasoning, extended to this page.
   The token, not the outline: the focus ring sets outline-color: #f97316
   explicitly, so it is untouched — and on light and Mix it is the only focus
   cue those fields have. TRIAL SCOPE: this page only, to confirm the look
   before it moves into qaShell.css for all of them. */
body.theme-mix .qa-page {
  --border-weak: 234 234 234;
}

.qa-sim-result {
  border: 1px solid var(--qa-border);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--qa-muted);
}

.qa-hint-block {
  padding: 10px 12px;
  border: 1px solid var(--qa-border);
  border-radius: 8px;
  background: var(--qa-muted);
}

.qa-hint-block .qa-hint {
  margin: 4px 0 0;
  word-break: break-word;
}

.qa-route-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.qa-route-card {
  border: 1px solid var(--qa-border);
  border-radius: 8px;
  background: var(--qa-muted);
  overflow: hidden;
}

.qa-route-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-bottom: 1px solid var(--qa-border);
  background: var(--qa-surface);
}

.qa-route-card-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.qa-route-id {
  font-size: 12px;
  font-weight: 600;
  color: var(--qa-text-secondary);
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 6px;
}

.qa-route-enabled {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--qa-text-secondary);
  cursor: pointer;
}

.qa-route-card-body {
  padding: 14px;
  background: var(--qa-surface);
}

/* Settings tab: friendly weekday chips + escalation destination checklist */
.ah-stack-field {
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.ah-daychips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.ah-daychip {
  padding: 5px 11px;
  border: 1px solid var(--qa-border);
  border-radius: 999px;
  background: var(--qa-surface);
  color: var(--qa-text-secondary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}
.ah-daychip:hover {
  border-color: var(--qa-accent);
}
.ah-daychip--on {
  background: var(--qa-accent);
  border-color: var(--qa-accent);
  color: #ffffff;
}
.ah-destpick {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
/* Same reason as .ah-opt below: a bare <label> inherits the near-white
   text-n-slate-12 on Mix, because Mix carries body.dark too. */
.ah-destpick-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--qa-text);
  cursor: pointer;
}
.ah-disc-state {
  color: var(--qa-text-secondary);
  font-weight: 400;
}
</style>

<style>
/* Mix keeps light cards; dark inherits --qa-* tokens from qaShell. */
body.theme-mix .qa-page .qa-route-card,
body.theme-mix .qa-page .qa-route-card-header,
body.theme-mix .qa-page .qa-route-card-body,
body.theme-mix .qa-page .qa-hint-block {
  background: #ffffff !important;
  color: #111827 !important;
  border-color: #e5e7eb !important;
}

body.theme-mix .qa-page .qa-route-card {
  background: #f9fafb !important;
}

body.theme-mix .qa-page .qa-route-id {
  background: #f3f4f6 !important;
  color: #374151 !important;
}

/* Mix carries body.dark as well, and these sit after the theme-mix block, so
 * without the guard they win on source order and darken a light page. */
body.dark:not(.theme-mix) .qa-page .qa-route-card {
  background: var(--qa-muted);
  border-color: var(--qa-border);
  color: var(--qa-text);
}

body.dark:not(.theme-mix) .qa-page .qa-route-card-header,
body.dark:not(.theme-mix) .qa-page .qa-route-card-body,
body.dark:not(.theme-mix) .qa-page .qa-hint-block {
  background: var(--qa-surface);
  border-color: var(--qa-border);
  color: var(--qa-text);
}

body.dark:not(.theme-mix) .qa-page .qa-route-id {
  background: rgba(255, 255, 255, 0.08);
  color: var(--qa-text-secondary);
}

/* ── Telegram connection cards (set A) ── */
.qa-page .ah-conns {
  display: grid;
  gap: 10px;
  margin-top: 14px;
}
.qa-page .ah-conn {
  display: grid;
  grid-template-columns: 42px 1fr auto;
  gap: 14px;
  align-items: start;
  background: var(--qa-surface);
  border: 1px solid var(--qa-border);
  border-radius: 12px;
  padding: 14px 16px;
  transition:
    box-shadow 0.16s ease,
    border-color 0.16s ease;
}
.qa-page .ah-conn:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
.qa-page .ah-conn--error {
  border-color: rgba(208, 59, 59, 0.5);
}
.qa-page .ah-avatar {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  font-size: 20px;
  background: var(--qa-accent-soft);
}
.qa-page .ah-avatar--error {
  background: rgba(208, 59, 59, 0.12);
}
.qa-page .ah-avatar--unverified {
  background: rgba(180, 83, 9, 0.12);
}
.qa-page .ah-name {
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
}
.qa-page .ah-id {
  margin-top: 4px;
  font-size: 13.5px;
  color: var(--qa-text-secondary);
}
.qa-page .ah-id b {
  color: var(--qa-text);
}
.qa-page .ah-err {
  margin-top: 6px;
  font-size: 12.5px;
  color: #d03b3b;
  background: rgba(208, 59, 59, 0.08);
  padding: 6px 10px;
  border-radius: 7px;
}
.qa-page .ah-sub {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 12.5px;
  color: var(--qa-text-muted);
  align-items: center;
}
.qa-page .ah-reach-ok {
  color: var(--qa-accent);
  font-weight: 600;
}
.qa-page .ah-reach-warn {
  color: #b45309;
  font-weight: 600;
}
.qa-page .ah-token code {
  font-size: 12px;
}
.qa-page .ah-actions {
  display: flex;
  gap: 8px;
}

/* ── Destinations: connect-a-group flow + destination cards ── */
.qa-page .ah-connect {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
}
.qa-page .ah-connect .qa-select {
  min-width: 260px;
}
.qa-page .ah-howto {
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  background: var(--qa-accent-soft);
  border: 1px solid var(--qa-border);
}
.qa-page .ah-howto-title {
  font-size: 13.5px;
  font-weight: 600;
}
.qa-page .ah-steps {
  margin: 8px 0 0;
  padding-left: 20px;
  font-size: 13px;
  color: var(--qa-text-secondary);
}
.qa-page .ah-steps li {
  margin-bottom: 4px;
}
.qa-page .ah-empty {
  margin-top: 14px;
  padding: 22px 18px;
  text-align: center;
  border: 1px dashed var(--qa-border);
  border-radius: 12px;
}
.qa-page .ah-empty-title {
  font-size: 14.5px;
  font-weight: 600;
}
.qa-page .ah-dests {
  display: grid;
  gap: 10px;
  margin-top: 14px;
}
.qa-page .ah-dest {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: start;
  background: var(--qa-surface);
  border: 1px solid var(--qa-border);
  border-radius: 12px;
  padding: 14px 16px;
}
.qa-page .ah-dest-head {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
}
.qa-page .ah-dest-title {
  font-weight: 600;
  font-size: 14.5px;
}
.qa-page .ah-dest-type {
  font-size: 12px;
  color: var(--qa-text-muted);
}
.qa-page .ah-dest-meta {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 12.5px;
  color: var(--qa-text-secondary);
}
.qa-page .ah-dest-warn {
  margin-top: 6px;
  font-size: 12.5px;
  color: #b45309;
}
.qa-page .ah-dest-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.qa-page .ah-dest-edit {
  grid-column: 1 / -1;
  display: grid;
  gap: 10px;
  margin-top: 4px;
  padding-top: 12px;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  border-top: 1px solid var(--qa-border);
}
.qa-page .ah-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12.5px;
  color: var(--qa-text-secondary);
}
.qa-page .ah-field--check {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

/* ── Routing: rule cards, coverage evidence and warnings ── */
.qa-page .ah-gap {
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(180, 83, 9, 0.08);
  border: 1px solid rgba(180, 83, 9, 0.28);
}
.qa-page .ah-gap-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #92400e;
}
.qa-page .ah-gap-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
  font-size: 13px;
}
.qa-page .ah-rule-title {
  font-weight: 600;
  font-size: 14.5px;
}
.qa-page .ah-warns {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 9px;
  background: rgba(180, 83, 9, 0.08);
  border: 1px solid rgba(180, 83, 9, 0.28);
}
.qa-page .ah-warns-title {
  font-size: 12.5px;
  font-weight: 600;
  color: #92400e;
}
.qa-page .ah-warns-list {
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: 12.5px;
  color: var(--qa-text-secondary);
}
.qa-page .ah-warns-list li {
  margin-bottom: 3px;
}
.qa-page .ah-cover {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding: 9px 12px;
  border-radius: 9px;
  background: var(--qa-muted);
  border: 1px solid var(--qa-border);
  font-size: 13px;
}
.qa-page .ah-cover-line {
  color: var(--qa-text);
}
.qa-page .ah-cover-reason {
  color: var(--qa-text-secondary);
  padding-left: 9px;
  border-left: 2px solid var(--qa-border);
}
.qa-page .ah-preview {
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 9px;
  font-size: 13px;
  background: var(--qa-accent-soft);
  border: 1px solid var(--qa-border);
}
/* The guide reuses .qa-details, .qa-table and the page's own text tokens, so it
   follows every theme without a palette of its own. What it does not reuse is
   .ah-gap-title: that amber is #92400e, which reads 6.4:1 on the light callout
   and 2.1:1 on the dark one, so the callouts below take the page text colour
   instead — 15:1 either way. */
.qa-page .ah-setup {
  margin-top: 0;
  margin-bottom: 14px;
}
.qa-page .ah-setup-body {
  padding: 14px 16px 16px;
}
.qa-page .ah-setup-body .qa-hint {
  margin: 4px 0 0;
}
.qa-page .ah-setup-body .qa-desc {
  margin: 4px 0 0;
}
.qa-page .ah-setup-head {
  margin: 22px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--qa-text);
}
/* Renamed off .ah-steps: the Destinations tab already had a list under that
   name, and an identical selector added later quietly restyled it. */
.qa-page .ah-guide-steps {
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  counter-reset: ah-step;
}
.qa-page .ah-guide-steps > li {
  counter-increment: ah-step;
  position: relative;
  margin-left: 11px;
  padding: 0 0 18px 30px;
  border-left: 1px solid var(--qa-border);
  font-size: 13px;
  line-height: 1.55;
  color: var(--qa-text-secondary);
}
/* The rail is the thread between steps, so it must not hang past the last one. */
.qa-page .ah-guide-steps > li:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}
.qa-page .ah-guide-steps > li::before {
  content: counter(ah-step);
  position: absolute;
  left: -11px;
  top: -1px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Reversed page tokens, so the disc follows the theme: 17.7:1 on light and
     Mix, 16.2:1 on dark. */
  background: var(--qa-text);
  color: var(--qa-surface);
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1;
}
.qa-page .ah-step-title {
  display: block;
  margin-bottom: 3px;
  font-weight: 600;
  color: var(--qa-text);
}
.qa-page .ah-guide-steps p {
  margin: 0 0 6px;
}
.qa-page .ah-guide-steps p:last-child {
  margin-bottom: 0;
}
/* A transcript of what BotFather actually says. Prose could carry the same
   facts, and did — but someone who has never made a bot cannot picture the
   screen from a sentence, and the two answers it wants are easy to mix up.
   Each line is its own span so the block keeps its shape without depending on
   the source formatting. */
.qa-page .ah-chat {
  margin: 0 0 8px;
  padding: 9px 11px;
  border-radius: 7px;
  background: var(--qa-muted);
  border: 1px solid var(--qa-border);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.7;
  color: var(--qa-text-secondary);
  overflow-x: auto;
}
.qa-page .ah-chat span {
  display: inline;
}
.qa-page .ah-chat .ah-chat-you {
  display: block;
  color: var(--qa-text);
  font-weight: 600;
}
.qa-page .ah-chat .ah-chat-bot {
  display: block;
  color: var(--qa-accent);
  font-weight: 600;
}
.qa-page .ah-chat .ah-chat-note {
  color: var(--qa-text-muted);
}
/* The token line has no speakers, so it drops the per-line breaks. */
.qa-page .ah-chat--plain {
  color: var(--qa-text);
  white-space: nowrap;
}
.qa-page .ah-guide-steps code,
.qa-page .ah-setup-body code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  padding: 1px 5px;
  border-radius: 4px;
  background: var(--qa-muted);
  border: 1px solid var(--qa-border);
  color: var(--qa-text);
}
.qa-page .ah-setup-warn {
  display: block;
  margin-top: 7px;
  padding: 8px 11px;
  border-radius: 8px;
  background: rgba(180, 83, 9, 0.08);
  border: 1px solid rgba(180, 83, 9, 0.28);
  color: var(--qa-text);
}
.qa-page .ah-setup-body .qa-table-wrap {
  margin-top: 8px;
}
.qa-page .ah-opt-block {
  margin-top: 14px;
}
.qa-page .ah-opts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}
/* The colour is not decoration. These are <label> elements, and _base.scss
   gives every bare label `text-n-slate-12`. Mix carries body.dark as well as
   body.theme-mix, so that token resolves to the DARK palette's near-white
   (237 238 240) while the pill underneath stays Mix's white surface — 1.16:1,
   which is to say invisible. Naming the QA token instead pins it per theme:
   #111827 on light and Mix, #ffffff on dark. */
.qa-page .ah-opt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 12.5px;
  color: var(--qa-text);
  background: var(--qa-surface);
  border: 1px solid var(--qa-border);
}
.qa-page .ah-opt:hover {
  border-color: var(--qa-accent);
}
.qa-page .ah-sched-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
/* bottom breathing room so content never collides with the floating Collapse button */
.qa-page .qa-tab-panel {
  padding-bottom: 52px;
}
</style>
